import { CrearSalaComponent } from '../crear-sala/crear-sala.component';
import { Component, computed, signal, OnInit, DestroyRef, inject, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { BusinessConfigService } from '../../services/business-config.service';
import { friendlyError } from '../../utils/friendly-error';
import { SalaService } from '../../services/sala.service';
import { SubscriptionService } from '../../services/subscription.service';
import { ReservationService } from '../../services/reservation.service';
import { Espacio, EspacioImage, SuperAdminBusiness, Reservation, BusinessPrivacyInfo } from '../../models/sala.model';
import { NoveltyService } from '../../services/novelty.service';
import { ReservationEditService } from '../../services/reservation-edit.service';
import { PlatformContactsService } from '../../services/platform-contacts.service';

import { ReservasComponent } from '../reservas/reservas.component';
import { CalendarioComponent } from '../calendario/calendario.component';
import { UsuariosComponent } from '../usuarios/usuarios.component';
import { ConfiguracionComponent } from '../configuracion/configuracion.component';
import { SuscripcionComponent } from '../suscripcion/suscripcion.component';
import { SuperAdminComponent } from '../superadmin/superadmin.component';
import { EstadisticasComponent } from '../estadisticas/estadisticas.component';
import { ReciclajeComponent } from '../reciclaje/reciclaje.component';
import { EmployeeService } from '../../services/employee.service';
import { EmpleadosComponent } from '../empleados/empleados.component';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { NovedadesComponent } from '../novedades/novedades.component';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { NovedadesSuperadminComponent } from '../novedades-superadmin/novedades-superadmin.component';
import { UiStateComponent } from '../ui/ui-state.component';
import { UiSkeletonComponent } from '../ui/ui-skeleton.component';
import { formatSpacesLabel } from '../../utils/format';
import { IngresosNegocioComponent } from '../ingresos-negocio/ingresos-negocio.component';
import { OwnerOnboardingComponent, OnboardingModule } from '../onboarding/owner-onboarding.component';
import { MiSitioComponent } from '../mi-sitio/mi-sitio.component';
import { MiSitioUnsavedChangesService } from '../../services/mi-sitio-unsaved-changes.service';
import { environment } from '../../../environments/environment';
import { DashboardService, StatKey } from '../../services/dashboard.service';
import { DashboardHeroComponent } from './sections/dashboard-hero.component';
import { DashboardStatsComponent } from './sections/dashboard-stats.component';
import { DashboardAgendaComponent } from './sections/dashboard-agenda.component';
import { DashboardNextReservationComponent } from './sections/dashboard-next-reservation.component';
import { DashboardTasksComponent } from './sections/dashboard-tasks.component';
import { DashboardSpacesComponent, SpaceMenuAction } from './sections/dashboard-spaces.component';
import { AvailabilityBlocksComponent } from '../availability-blocks/availability-blocks.component';

type DashboardTab = 'overview' | 'salas' | 'reservas' | 'calendario' | 'usuarios' | 'reciclaje' | 'estadisticas' | 'configuracion' | 'miSitio' | 'ingresosNegocio' | 'subadministrador' | 'novedades' | 'superadminNovedades' | 'suscripcion' | 'superadmin' | 'reembolsos';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, CrearSalaComponent, ReservasComponent, CalendarioComponent, UsuariosComponent, ConfiguracionComponent, MiSitioComponent, IngresosNegocioComponent, OwnerOnboardingComponent, SuscripcionComponent, SuperAdminComponent, EstadisticasComponent, ReciclajeComponent, EmpleadosComponent, NovedadesComponent, NovedadesSuperadminComponent, UiStateComponent, UiSkeletonComponent, CooldownBubbleDirective, DashboardHeroComponent, DashboardStatsComponent, DashboardAgendaComponent, DashboardNextReservationComponent, DashboardTasksComponent, DashboardSpacesComponent, AvailabilityBlocksComponent],
  template: `

<div class="dashboard-container">
        <nav class="dashboard-nav" *ngIf="false">
          <div class="sidebar-brand">
            <img src="TuSala_LogoSinFondo.png" alt="TuSala Logo" class="logo_dashboard">
          </div>
          <span class="nav-section-label">Principal</span>
          <button (click)="selectTab('overview')" [class.active]="activeTab() === 'overview'" class="nav-btn">
            <i class="bi bi-grid-1x2"></i><span>Dashboard</span>
          </button>
          <button *ngIf="currentUser()?.role === 'owner' || isSuperAdmin()" (click)="selectTab('ingresosNegocio')" [class.active]="activeTab() === 'ingresosNegocio'" class="nav-btn"><i class="bi bi-wallet2"></i><span>Ingresos y transferencias</span></button>
          <button 
            *ngIf="can('spaces')"
            (click)="selectTab('salas')" 
            [class.active]="activeTab() === 'salas'"
            class="nav-btn"
          >
            <i class="bi bi-door-open"></i><span>Gestión de Salas</span>
          </button>
          <button 
            *ngIf="can('reservations')"
            (click)="selectTab('reservas')" 
            [class.active]="activeTab() === 'reservas'"
            class="nav-btn"
          >
            <i class="bi bi-calendar2-check"></i><span>Reservas</span>
          </button>
          <button
            *ngIf="can('reservations')"
            (click)="selectTab('calendario')"
            [class.active]="activeTab() === 'calendario'"
            class="nav-btn"
          >
            <i class="bi bi-calendar3"></i><span>Calendario</span>
          </button>
          <button 
            *ngIf="can('customers')"
            (click)="selectTab('usuarios')" 
            [class.active]="activeTab() === 'usuarios'"
            class="nav-btn"
          >
            <i class="bi bi-people"></i><span>Clientes</span>
          </button>
          <button 
            *ngIf="can('statistics')"
            (click)="selectTab('estadisticas')" 
            [class.active]="activeTab() === 'estadisticas'"
            class="nav-btn"
          >
            <i class="bi bi-bar-chart-line"></i><span>Estadísticas</span>
          </button>
          <button
            *ngIf="can('recycle')"
            (click)="selectTab('reciclaje')"
            [class.active]="activeTab() === 'reciclaje'"
            class="nav-btn"
          >
            <i class="bi bi-trash3"></i><span>Papelera</span>
          </button>
          <span class="nav-section-label">Administración</span>
          <button 
            *ngIf="can('settings')"
            (click)="selectTab('configuracion')" 
            [class.active]="activeTab() === 'configuracion'"
            class="nav-btn"
          >
            <i class="bi bi-gear"></i><span>Configuración</span>
          </button>
          <button
            *ngIf="can('employees')"
            (click)="selectTab('subadministrador')"
            [class.active]="activeTab() === 'subadministrador'"
            class="nav-btn"
          >
            <i class="bi bi-person-gear"></i><span>Sub-Administrador</span>
          </button>
          <button (click)="selectTab('novedades')" [class.active]="activeTab() === 'novedades'" class="nav-btn"><i class="bi bi-megaphone"></i><span>Novedades</span><span class="badge" *ngIf="businessUnread()">{{businessUnread()}}</span></button>
          <button 
            *ngIf="currentUser()?.role !== 'employee' && !currentUser()?.subscription_exempt"
            (click)="selectTab('suscripcion')" 
            [class.active]="activeTab() === 'suscripcion'"
            class="nav-btn"
          >
            <i class="bi bi-gem"></i><span>Mi Suscripción</span>
          </button>
          <span class="nav-section-label" *ngIf="isSuperAdmin()">Super Admin</span>
          <button 
            *ngIf="isSuperAdmin()"
            (click)="showSuperAdmin()" 
            [class.active]="superAdminOpen()"
            class="nav-btn nav-btn-admin"
          >
            <i class="bi bi-shield-check"></i><span>Centro de operaciones</span>
          </button>
          <div *ngIf="isSuperAdmin() && superAdminOpen()" class="superadmin-menu">
            <button *ngFor="let section of superAdminSections" (click)="openSuperAdmin(section.id)" [class.active]="activeTab() === 'superadmin' && superAdminSection() === section.id"><i [class]="section.icon" aria-hidden="true"></i><span>{{ section.label }}</span></button>
            <button (click)="selectTab('superadminNovedades')" [class.active]="activeTab() === 'superadminNovedades'"><i class="bi bi-megaphone" aria-hidden="true"></i><span>Novedades globales</span><span class="badge" *ngIf="globalUnread()">{{globalUnread()}}</span></button>
          </div>
          <div class="sidebar-footer"><button class="logout-btn" (click)="openLogoutConfirm()"><i class="bi bi-box-arrow-right"></i><span>Cerrar sesión</span></button></div>
        </nav>
        
        <main class="dashboard-content" [class.has-paginated-module]="hasPaginatedModule()">
          <app-owner-onboarding
            *ngIf="(currentUser()?.role === 'owner' || currentUser()?.role === 'super_admin') && !currentUser()?.force_password_change && !isBusinessContextMode()"
            [businessId]="currentUser()?.id || 0"
            [userId]="currentUser()?.session_user?.user_id || 0"
            [userEmail]="currentUser()?.session_user?.email || currentUser()?.email || ''"
            [configurationExempt]="isPrimaryPlatformAdmin()"
            [role]="currentUser()?.role || ''"
            [plan]="subscriptionPlan() || currentUser()?.subscription?.plan || ''"
            [firstLogin]="!!currentUser()?.is_first_login"
            [required]="!!currentUser()?.onboarding_required"
            [businessName]="businessName()"
            [ownerName]="firstName()"
            [currentModule]="activeTab()"
            (navigate)="navigateFromOnboarding($event)">
          </app-owner-onboarding>
          <div class="page-heading">
            <div><span class="breadcrumb">TuSala / {{ moduleTitle() }}</span><h1>{{ moduleTitle() }}</h1><p>{{ moduleDescription() }}</p></div>
            <div *ngIf="activeTab() === 'salas'" class="spaces-heading-actions">
              <button
                type="button"
                class="featured-listing-summary"
                [class.is-upgrade]="!salaService.featuredListingEnabled()"
                (click)="openFeaturedBenefit()"
                [attr.aria-label]="salaService.featuredListingEnabled() ? 'Resumen de espacios destacados' : 'Conocer el beneficio Premium para destacar espacios'"
              >
                <span class="featured-summary-icon"><i class="bi bi-stars" aria-hidden="true"></i></span>
                <span class="featured-summary-copy">
                  <strong>Destacar en galería</strong>
                  <small>{{ salaService.featuredListingEnabled() ? (salaService.featuredListingSelectedCount() + ' de ' + salaService.featuredListingLimit() + ' seleccionados') : 'Beneficio Premium' }}</small>
                </span>
                <i *ngIf="!salaService.featuredListingEnabled()" class="bi bi-chevron-right featured-summary-arrow" aria-hidden="true"></i>
              </button>
              <button
                *ngIf="canUseOwnSite()"
                type="button"
                class="btn-secondary"
                [disabled]="mySiteSharing()"
                (click)="shareMySiteWebsite()"
              ><i class="bi" [class.bi-share-fill]="!mySiteSharing()" [class.bi-arrow-repeat]="mySiteSharing()" aria-hidden="true"></i>{{ mySiteSharing() ? 'Preparando enlace...' : 'Compartir mi sitio web' }}</button>
              <button type="button" class="btn-secondary" (click)="showAvailabilityBlocks.set(true)" title="Fechas u horarios en que tus espacios no reciben reservas (asueto, mantenimiento, emergencias)"><i class="bi bi-calendar-x" aria-hidden="true"></i>Días no disponibles</button>
              <button type="button" class="btn-secondary" (click)="openSpaceDrafts()" title="Borradores de espacios guardados por tu equipo"><i class="bi bi-journal-text" aria-hidden="true"></i>Borradores</button>
              <button class="btn-primary" (click)="createEspacio()"><i class="bi bi-plus-lg"></i>Nuevo espacio</button>
            </div>
            <div *ngIf="activeTab() === 'reservas'" class="spaces-heading-actions">
              <label class="page-size-control">Registros por página
                <select [value]="reservasRef?.reservationsPageSize()" (change)="onReservationsPageSizeChange($event)">
                  <option *ngFor="let size of reservasRef?.reservationsPageSizeOptions || [25, 50, 75, 100]" [value]="size">{{ size }}</option>
                </select>
              </label>
              <button class="btn-primary" (click)="openNewReservation()"><i class="bi bi-plus-lg"></i>Nueva reserva</button>
            </div>
          </div>
          <section *ngIf="activeTab() === 'overview'" class="control-overview">
            <app-dashboard-hero
              [firstName]="firstName()"
              [businessName]="businessName()"
              [imageUrl]="dash.heroImage()"
              [tip]="dash.tip()"
              [loading]="dash.spacesLoading()"
              [canCreateReservation]="can('reservations')"
              [canManageSpaces]="can('spaces')"
              (createReservation)="openNewReservation()"
              (manageSpaces)="openModule('salas')"
              (openTip)="openModule($event)">
            </app-dashboard-hero>

            <app-dashboard-stats
              [reservationsToday]="dash.dayCounts()?.today ?? null"
              [reservationsYesterday]="dash.dayCounts()?.yesterday ?? null"
              [spacesActive]="dash.activeSpaces().length"
              [spacesTotal]="dash.spacesTotal()"
              [customersTotal]="dash.customersTotal()"
              [paymentsPending]="dash.pendingPayments()"
              [loading]="dash.statsLoading()"
              [canReservations]="can('reservations')"
              [canSpaces]="can('spaces')"
              [canCustomers]="can('customers')"
              (select)="onStatSelect($event)">
            </app-dashboard-stats>

            <div class="overview-main" [class.no-agenda]="!can('reservations')">
              <div class="overview-primary">
                <app-dashboard-agenda
                  *ngIf="can('reservations')"
                  [rows]="dash.agendaRows()"
                  [windowStart]="dash.agendaWindow().start"
                  [windowEnd]="dash.agendaWindow().end"
                  [selectedDate]="dash.selectedDate()"
                  [loading]="dash.calendarLoading()"
                  [error]="dash.calendarError()"
                  [compact]="compactLayout()"
                  (prev)="shiftDay(-1)"
                  (next)="shiftDay(1)"
                  (today)="goToToday()"
                  (openCalendar)="openModule('calendario')"
                  (openReservation)="onEditReservationFromCalendar($event)"
                  (openSpace)="viewDetalle($event)"
                  (retry)="dash.reloadAgenda()">
                </app-dashboard-agenda>

                <app-dashboard-spaces
                  *ngIf="can('spaces')"
                  [spaces]="dash.spaces()"
                  [total]="dash.spacesTotal()"
                  [loading]="dash.spacesLoading()"
                  [error]="!!dash.spacesError()"
                  [busySpaceIds]="dash.todayBusySpaceIds()"
                  [canCreateReservation]="can('reservations')"
                  [canEdit]="can('spaces')"
                  (details)="viewDetalle($event.space_id)"
                  (newReservation)="openNewReservation($event)"
                  (seeAll)="openModule('salas')"
                  (retry)="dash.reloadSpaces()"
                  (menu)="onSpaceMenu($event)">
                </app-dashboard-spaces>
              </div>

              <aside class="overview-side" *ngIf="can('reservations')">
                <app-dashboard-next-reservation
                  [reservation]="dash.nextReservation()"
                  [imageUrl]="nextReservationImage()"
                  (details)="onEditReservationFromCalendar($event)"
                  (contact)="contactCustomer($event)"
                  (calendar)="openModule('calendario')">
                </app-dashboard-next-reservation>

                <app-dashboard-tasks [tasks]="dash.tasks()" (run)="openModule($event)"></app-dashboard-tasks>

              </aside>
            </div>
          </section>
          <div *ngIf="activeTab() === 'salas'" class="tab-content">
            <div class="salas-container">
              <ng-container *ngIf="false; else ownerSpaces">
                <div class="salas-header">
                  <h2>Negocios registrados</h2>
                </div>

                <div *ngIf="businessesLoading()" class="empty-state">
                  <p>Cargando negocios...</p>
                </div>

                <div *ngIf="!businessesLoading() && businesses().length === 0" class="empty-state">
                  <p>No hay negocios registrados.</p>
                </div>

                <div class="business-list" *ngIf="!businessesLoading() && businesses().length > 0">
                  <button type="button" class="business-row" *ngFor="let business of businesses()" (click)="openBusinessModal(business)">
                    <div>
                      <strong>{{ business.name }}</strong>
                      <span>{{ business.company }}</span>
                    </div>
                    <div>
                      <span>{{ business.owner?.name || 'Sin owner' }}</span>
                      <small>{{ business.email }}</small>
                    </div>
                    <span class="status-badge" [class.active]="business.status" [class.unavailable]="!business.status">
                      {{ business.status ? 'Activo' : 'Suspendido' }}
                    </span>
                  </button>
                </div>

                <div class="modal-overlay" *ngIf="selectedBusiness()" (click)="closeBusinessModal()">
                  <div class="business-modal" (click)="$event.stopPropagation()">
                    <div class="modal-header">
                      <h3>{{ selectedBusiness()?.name }}</h3>
                      <button type="button" class="close-btn" (click)="closeBusinessModal()">×</button>
                    </div>
                    <div class="business-info-grid">
                      <div><label>Empresa</label><span>{{ selectedBusiness()?.company || '—' }}</span></div>
                      <div><label>Email</label><span>{{ selectedBusiness()?.email || '—' }}</span></div>
                      <div><label>Telefono</label><span>{{ selectedBusiness()?.phone || '—' }}</span></div>
                      <div><label>Estado</label><span>{{ selectedBusiness()?.status ? 'Activo' : 'Suspendido' }}</span></div>
                      <div><label>Owner</label><span>{{ selectedBusiness()?.owner?.name || 'Sin owner' }}</span></div>
                      <div><label>Email owner</label><span>{{ selectedBusiness()?.owner?.email || '—' }}</span></div>
                      <div><label>Plan</label><span>{{ selectedBusiness()?.subscription?.plan ? formatPlanLabel(selectedBusiness()?.subscription?.plan) : 'Sin suscripcion' }}</span></div>
                      <div><label>Suscripcion</label><span>{{ formatSubscriptionStatusLabel(selectedBusiness()?.subscription?.status) }}</span></div>
                    </div>
                  </div>
                </div>
              </ng-container>

              <ng-template #ownerSpaces>

              <div class="space-search">
                <i class="bi bi-search" aria-hidden="true"></i>
                <input #spaceSearchInput type="search" [value]="spaceSearch()" placeholder="Buscar por nombre, categoría o ubicación" (keyup.enter)="searchSpaces(spaceSearchInput.value)" />
                <button type="button" [disabled]="salaService.espaciosLoading()||cooldown.getSignal('spaces-search')()" (click)="searchSpaces(spaceSearchInput.value)" cooldownBubble data-cooldown-key="spaces-search"><i class="bi" [class.bi-search]="!salaService.espaciosLoading()" [class.bi-arrow-repeat]="salaService.espaciosLoading()"></i>{{ salaService.espaciosLoading() ? 'Buscando...' : 'Buscar' }}</button>
                <button type="button" class="clear" *ngIf="spaceSearch()" [disabled]="salaService.espaciosLoading()" (click)="clearSpaceSearch(spaceSearchInput)">Limpiar</button>
              </div>

              <app-ui-skeleton *ngIf="salaService.espaciosLoading()" variant="cards" [count]="3" [label]="spaceSearch() ? 'Buscando espacios' : 'Cargando espacios'"></app-ui-skeleton>

              <app-ui-state *ngIf="!salaService.espaciosLoading() && salaService.espaciosError()" tone="error" title="No pudimos cargar los espacios" [message]="salaService.espaciosError() || ''" actionLabel="Reintentar" (action)="reloadEspacios()"></app-ui-state>

              <app-ui-state *ngIf="!salaService.espaciosLoading() && !salaService.espaciosError() && salaService.espacios().length === 0 && !spaceSearch()" tone="empty" icon="bi bi-building" kubo="explicando" title="Aún no tienes espacios" message="Publica tu primer espacio y comienza a recibir reservas." actionLabel="Publicar ahora" (action)="createEspacio()"></app-ui-state>
              <app-ui-state *ngIf="!salaService.espaciosLoading() && !salaService.espaciosError() && salaService.espacios().length === 0 && !!spaceSearch()" tone="empty" title="No encontramos espacios" [message]="'No hay resultados para “' + spaceSearch() + '”. Prueba con otro nombre, categoría o ubicación.'" actionLabel="Limpiar búsqueda" (action)="clearSpaceSearch(spaceSearchInput)"></app-ui-state>

              <div class="espacios-grid" *ngIf="!salaService.espaciosLoading() && salaService.espaciosInitialized() && salaService.espacios().length > 0">
                <div *ngFor="let espacio of salaService.espacios()" class="espacio-card" (click)="viewDetalle(espacio.space_id)">
                  <div class="card-image">
                    <img 
                      *ngIf="espacio.images && espacio.images.length > 0 && !failedSpaceImages.has(espacio.space_id)"
                      [src]="getImageUrl(getCoverImage(espacio).image_url)"
                      [alt]="espacio.title"
                      (error)="handleImageError(espacio.space_id)"
                    />
                    <div *ngIf="!espacio.images || espacio.images.length === 0 || failedSpaceImages.has(espacio.space_id)" class="no-image">
                      <span class="no-image-art" aria-hidden="true">
                        <i class="bi bi-building"></i>
                        <span class="no-image-ring ring-one"></span>
                        <span class="no-image-ring ring-two"></span>
                      </span>
                      <strong>Sin fotografía del espacio</strong>
                      <small>Agrega una imagen real para completar su presentación</small>
                    </div>
                    <span class="status-badge" [class.active]="espacio.is_active" [class.unavailable]="!espacio.is_active">
                      {{ espacio.is_active ? 'Disponible' : 'No disponible' }}
                    </span>
                    <span class="featured-badge" *ngIf="espacio.is_featured_listing">
                      <i class="bi bi-star-fill" aria-hidden="true"></i> Destacado
                    </span>
                  </div>

                  <div class="card-content">
                    <span class="visibility-chip" *ngIf="spaceVisibility(espacio) as visibility" [class.is-private]="visibility.private" [title]="visibility.hint">
                      <i class="bi" [class.bi-shield-lock-fill]="visibility.private" [class.bi-globe2]="!visibility.private" aria-hidden="true"></i> {{ visibility.label }}
                    </span>
                    <h3>{{ espacio.title }}</h3>
                    <p class="description" [title]="espacio.description" [attr.aria-label]="espacio.description">{{ espacio.description }}</p>
                    
                    <div class="card-details">
                      <div class="detail-item category-item">
                        <i class="bi bi-tag" aria-hidden="true"></i>
                        <span [title]="categoryLabels(espacio)" [attr.aria-label]="'Categorías: ' + categoryLabels(espacio)">{{ visibleCategoryLabels(espacio) }}</span>
                        <small *ngIf="remainingCategoryCount(espacio) > 0">+{{ remainingCategoryCount(espacio) }} más</small>
                      </div>
                      <div class="detail-item">
                        <i class="bi bi-people" aria-hidden="true"></i>
                        <span>{{ espacio.capacity }} personas</span>
                      </div>
                      
                      <div class="detail-item">
                        <i class="bi bi-currency-dollar" aria-hidden="true"></i>
                        <span>{{ espacio.price_per_hour }}/hora</span>
                      </div>
                      
                      <div class="detail-item">
                        <i class="bi bi-geo-alt" aria-hidden="true"></i>
                        <span>{{ espacio.city }}, {{ espacio.country }}</span>
                      </div>
                    </div>

                    <div class="extras-section" [class.is-empty]="!espacio.extras || espacio.extras.length === 0">
                      <h4>{{ espacio.extras && espacio.extras.length > 0 ? 'Extras disponibles' : 'Sin extras agregados' }}</h4>
                      <div class="extras-list" *ngIf="espacio.extras && espacio.extras.length > 0">
                        <span *ngFor="let extra of espacio.extras" class="extra-tag">
                          {{ extra.name }}
                        </span>
                      </div>
                    </div>
                    
                    <div class="card-actions">
                      <button
                        class="btn-featured"
                        [class.active]="espacio.is_featured_listing"
                        [title]="!espacio.is_active ? 'Publica el espacio para poder destacarlo' : (!espacio.is_featured_listing && salaService.featuredListingSelectedCount() >= salaService.featuredListingLimit() ? 'Ya alcanzaste el límite de espacios destacados' : (espacio.is_featured_listing ? 'Quitar de destacados' : 'Destacar en galería'))"
                        [attr.aria-label]="espacio.is_featured_listing ? 'Quitar de destacados' : 'Destacar en galería'"
                        [disabled]="salaService.featuredListingEnabled() && (featuredSpaceSavingId() === espacio.space_id || !espacio.is_active || (!espacio.is_featured_listing && salaService.featuredListingSelectedCount() >= salaService.featuredListingLimit()))"
                        (click)="handleFeaturedSpace(espacio); $event.stopPropagation()"
                      >
                        <i class="bi" [class.bi-star-fill]="espacio.is_featured_listing" [class.bi-star]="!espacio.is_featured_listing" aria-hidden="true"></i>
                      </button>
                      <button
                        *ngIf="businessPrivacyInfo()?.featureEnabled && !businessPrivacyInfo()?.isPrivate"
                        class="btn-secondary"
                        [class.danger]="espacio.is_private"
                        [title]="espacio.is_private ? 'Hacer público (ahora solo se ve con el código de acceso)' : 'Privatizar espacio (ocultarlo de la galería pública)'"
                        [attr.aria-label]="espacio.is_private ? 'Hacer público' : 'Privatizar espacio'"
                        [disabled]="privateSpaceSavingId() === espacio.space_id"
                        (click)="togglePrivateSpace(espacio); $event.stopPropagation()"
                      >
                        <i class="bi" [class.bi-shield-lock-fill]="espacio.is_private" [class.bi-shield-lock]="!espacio.is_private" aria-hidden="true"></i>
                      </button>
                      <button class="btn-secondary" title="Editar espacio" aria-label="Editar espacio" (click)="editEspacio(espacio.space_id); $event.stopPropagation()">
                        <i class="bi bi-pencil-square" aria-hidden="true"></i>
                      </button>
                      <button
                        class="btn-secondary"
                        [class.danger]="espacio.is_active"
                        [title]="togglingPublicationId() === espacio.space_id ? 'Procesando...' : (espacio.is_active ? 'Despublicar' : 'Publicar')"
                        [attr.aria-label]="espacio.is_active ? 'Despublicar' : 'Publicar'"
                        [disabled]="togglingPublicationId() === espacio.space_id"
                        (click)="requestSpacePublicationChange(espacio); $event.stopPropagation()"
                      >
                        <i class="bi" [class.bi-arrow-repeat]="togglingPublicationId() === espacio.space_id" [class.bi-eye-slash]="togglingPublicationId() !== espacio.space_id && espacio.is_active" [class.bi-eye]="togglingPublicationId() !== espacio.space_id && !espacio.is_active" aria-hidden="true"></i>
                      </button>
                      <button class="btn-secondary danger" title="Eliminar espacio" aria-label="Eliminar espacio" (click)="deleteEspacio(espacio.space_id); $event.stopPropagation()">
                        <i class="bi bi-trash3" aria-hidden="true"></i>
                      </button>
                      <button class="btn-secondary" [title]="copiedSpaceId() === espacio.space_id ? 'Copiado' : 'Copiar enlace'" [attr.aria-label]="copiedSpaceId() === espacio.space_id ? 'Copiado' : 'Copiar enlace'" (click)="copySpaceLink(espacio, $event)">
                        <i class="bi" [class.bi-clipboard]="copiedSpaceId() !== espacio.space_id" [class.bi-clipboard-check-fill]="copiedSpaceId() === espacio.space_id" aria-hidden="true"></i>
                      </button>
                      <button *ngIf="spaceVisibility(espacio)?.private" class="btn-secondary" title="Crear imagen para imprimir" aria-label="Crear imagen para imprimir" (click)="goToCreatePrintImage(espacio); $event.stopPropagation()">
                        <i class="bi bi-image" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </ng-template>
            </div>
          </div>

          <div *ngIf="activeTab() === 'reservas'" class="tab-content">
            <app-reservas #reservasRef></app-reservas>
          </div>

          <!-- Oculto temporalmente a pedido del usuario -- ver el
               comentario junto a la ruta '/reembolsos' en app.routes.ts.
          <div *ngIf="activeTab() === 'reembolsos'" class="tab-content">
            <app-refunds></app-refunds>
          </div>
          -->

          <div *ngIf="activeTab() === 'calendario'" class="tab-content">
            <app-calendario (editReservation)="onEditReservationFromCalendar($event)"></app-calendario>
          </div>
          
          <div *ngIf="activeTab() === 'usuarios'" class="tab-content">
            <app-usuarios></app-usuarios>
          </div>

          <div *ngIf="activeTab() === 'reciclaje'" class="tab-content">
            <app-reciclaje></app-reciclaje>
          </div>
          
          <div *ngIf="activeTab() === 'configuracion'" class="tab-content">
            <app-configuracion (irAMiSitio)="selectTab('miSitio')"></app-configuracion>
          </div>
          <div *ngIf="activeTab() === 'miSitio'" class="tab-content">
            <app-mi-sitio></app-mi-sitio>
          </div>
          <div *ngIf="activeTab() === 'ingresosNegocio' && (currentUser()?.role === 'owner' || isSuperAdmin())" class="tab-content"><app-ingresos-negocio></app-ingresos-negocio></div>

          <div *ngIf="activeTab() === 'subadministrador'" class="tab-content">
            <app-empleados></app-empleados>
          </div>

          <div *ngIf="activeTab() === 'novedades'" class="tab-content"><app-novedades></app-novedades></div>
          <div *ngIf="activeTab() === 'superadminNovedades' && isSuperAdmin()" class="tab-content"><app-novedades-superadmin></app-novedades-superadmin></div>

          <div *ngIf="activeTab() === 'estadisticas'" class="tab-content">
            <app-estadisticas
              [locked]="hasAdvancedStatistics() === false"
              [accessPending]="hasAdvancedStatistics() === null"
              [currentPlan]="subscriptionPlan()"
              [businessId]="currentUser()?.id || null"
              [businessName]="businessName()"
              (requestSubscription)="selectTab('suscripcion')">
            </app-estadisticas>
          </div>

          <div *ngIf="activeTab() === 'suscripcion'" class="tab-content">
            <app-suscripcion></app-suscripcion>
          </div>

          <div *ngIf="activeTab() === 'superadmin' && isSuperAdmin()" class="tab-content">
            <app-superadmin [initialTab]="superAdminSection()" [embeddedNavigation]="true"></app-superadmin>
          </div>
        </main>
        <div *ngIf="showFeaturedUpgrade()" class="modal-overlay featured-upgrade-overlay" (click)="closeFeaturedBenefit()">
          <section class="featured-upgrade-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-labelledby="featured-upgrade-title">
            <button type="button" class="featured-upgrade-close" (click)="closeFeaturedBenefit()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
            <span class="upgrade-spark spark-one" aria-hidden="true"></span>
            <span class="upgrade-spark spark-two" aria-hidden="true"></span>
            <span class="upgrade-spark spark-three" aria-hidden="true"></span>
            <div class="featured-upgrade-hero">
              <span class="featured-upgrade-icon"><i class="bi bi-stars"></i></span>
              <span class="featured-upgrade-label"><i class="bi bi-gem"></i> Beneficio Premium</span>
              <h2 id="featured-upgrade-title">Haz que tus mejores espacios brillen</h2>
              <p>Destácalos en la galería de clientes para ganar visibilidad y aumentar sus oportunidades de reserva.</p>
            </div>
            <div class="featured-upgrade-benefits">
              <span><i class="bi bi-check2-circle"></i><strong>Mayor exposición</strong><small>Presencia prioritaria ante clientes.</small></span>
              <span><i class="bi bi-check2-circle"></i><strong>Más oportunidades</strong><small>Impulsa el descubrimiento de tus espacios.</small></span>
              <span><i class="bi bi-check2-circle"></i><strong>Control flexible</strong><small>Elige qué espacios deseas destacar.</small></span>
            </div>
            <div class="featured-upgrade-actions">
              <button type="button" class="featured-upgrade-secondary" (click)="closeFeaturedBenefit()">Quizá después</button>
              <button type="button" class="featured-upgrade-primary" (click)="goToPremiumPlans()"><span>Mejorar mi plan</span><i class="bi bi-arrow-right"></i></button>
            </div>
          </section>
        </div>
        <!-- Días no disponibles del negocio completo o de cualquier espacio. -->
        <div *ngIf="showAvailabilityBlocks()" class="modal-overlay availability-blocks-overlay" (click)="showAvailabilityBlocks.set(false)">
          <div class="availability-blocks-modal" role="dialog" aria-modal="true" aria-label="Días no disponibles" (click)="$event.stopPropagation()">
            <button type="button" class="availability-blocks-close" (click)="showAvailabilityBlocks.set(false)" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
            <app-availability-blocks [canEdit]="can('spaces', 'edit')"></app-availability-blocks>
          </div>
        </div>
        <!-- Crear espacio: modal grande por pasos (con borrador). -->
        <app-crear-sala *ngIf="showCreateSpace()" [openDraftsOnStart]="createSpaceWithDrafts()" (closed)="showCreateSpace.set(false)"></app-crear-sala>
        <div *ngIf="showSpaceLimitModal()" class="modal-overlay featured-upgrade-overlay" (click)="closeSpaceLimitModal()">
          <section class="featured-upgrade-modal space-limit-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-labelledby="space-limit-title">
            <button type="button" class="featured-upgrade-close" (click)="closeSpaceLimitModal()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
            <div class="featured-upgrade-hero">
              <span class="featured-upgrade-icon"><i class="bi bi-buildings"></i></span>
              <span class="featured-upgrade-label"><i class="bi bi-info-circle"></i> Capacidad alcanzada</span>
              <h2 id="space-limit-title">Ya utilizaste {{ formatSpacesLabel(effectiveSpaceLimit()) }} de tu plan</h2>
              <p *ngIf="!isTrialPlan()">Tu plan {{ formatPlanLabel(subscriptionPlan()) }} sigue activo, pero para publicar otro espacio necesitas ampliar su capacidad.</p>
              <p *ngIf="isTrialPlan()">Tu cuenta de prueba sigue activa, pero para publicar otro espacio necesitas contratar tu plan definitivo.</p>
            </div>
            <div class="space-upgrade-options">
              <article *ngFor="let plan of suggestedSpacePlans()"><span>{{ plan.name || formatPlanLabel(plan.code) }}</span><strong>{{ planSpaceLimitLabel(plan) }}</strong><small>Desde &#36;{{ plan.price_monthly | number:'1.2-2' }} al mes</small></article>
            </div>
            <div class="custom-space-quote"><i class="bi bi-headset"></i><div><strong>¿Necesitas una cantidad personalizada?</strong><p>Administración puede asignar más espacios y preparar un precio mensual o anticipo acorde a tu negocio.</p><a [href]="'mailto:' + (contacts.contacts()?.administration_email || 'administracion@tusala.net') + '?subject=Cotización%20de%20espacios%20adicionales'">Contactar a administración</a></div></div>
            <div class="featured-upgrade-actions"><button type="button" class="featured-upgrade-secondary" (click)="closeSpaceLimitModal()">Entendido</button><button type="button" class="featured-upgrade-primary" (click)="goToSubscriptionPlans()"><span>Ver planes disponibles</span><i class="bi bi-arrow-right"></i></button></div>
          </section>
        </div>
        <div *ngIf="showLogoutConfirm()" class="modal-overlay logout-confirm" (click)="closeLogoutConfirm()">
          <section class="logout-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <span class="logout-icon"><i class="bi bi-box-arrow-right"></i></span><h3>¿Cerrar sesión?</h3><p>Tu sesión actual terminará y tendrás que iniciar sesión nuevamente para continuar.</p>
            <div><button class="btn-secondary" (click)="closeLogoutConfirm()">Cancelar</button><button class="logout-confirm-btn" (click)="logout()">Sí, cerrar sesión</button></div>
          </section>
        </div>
        <div *ngIf="spacePendingDeletion() as space" class="modal-overlay logout-confirm" (click)="cancelDeleteSpace()">
          <section class="logout-modal delete-space-modal critical-action-modal" (click)="$event.stopPropagation()" role="alertdialog" aria-modal="true">
            <span class="logout-icon"><i class="bi bi-trash3"></i></span><span class="critical-label">Acción crítica</span><h3>¿Enviar espacio a la Papelera?</h3><p><strong>{{ space.title }}</strong> dejará de mostrarse. Validaremos reservas activas, clientes y pagos relacionados antes de completar la acción.</p><div class="critical-note"><i class="bi bi-clock-history"></i>Podrás restaurarlo durante los próximos 30 días.</div>
            <div><button class="btn-secondary" [disabled]="deletingSpace()" (click)="cancelDeleteSpace()">Cancelar</button><button class="logout-confirm-btn delete-space-btn" [disabled]="deletingSpace()" (click)="confirmDeleteSpace()"><i *ngIf="deletingSpace()" class="bi bi-arrow-clockwise spin"></i>{{ deletingSpace() ? 'Enviando a la Papelera…' : 'Enviar a la Papelera' }}</button></div>
          </section>
        </div>
        <div *ngIf="currentUser()?.force_password_change" class="modal-overlay forced-password">
          <section class="forced-password-card" role="dialog" aria-modal="true" aria-labelledby="force-password-title">
            <header class="forced-password-hero">
              <span class="forced-password-icon"><i class="bi bi-shield-lock"></i></span>
              <div>
                <small>PRIMER ACCESO SEGURO</small>
                <h3 id="force-password-title">Restablece tu contraseña</h3>
                <p>Para continuar, te enviaremos un enlace seguro a tu correo para crear una contraseña nueva sin compartir ninguna contraseña temporal.</p>
              </div>
            </header>
            <div class="forced-password-notice"><i class="bi bi-info-circle"></i><span>El enlace se envía al correo asociado a tu cuenta y te permitirá cambiar la contraseña en el siguiente paso.</span></div>
            <div class="forced-password-email" *ngIf="currentUser()?.email as email">
              <i class="bi bi-envelope-check"></i>
              <span>{{ email }}</span>
            </div>
            <div class="forced-password-actions">
              <button type="button" class="forced-password-secondary" (click)="logout()">Cerrar sesión</button>
              <button type="button" class="forced-password-submit" [disabled]="resendingResetLink()" (click)="requestPasswordRecovery()">
                <span *ngIf="!resendingResetLink()">Reenviar enlace seguro</span>
                <span *ngIf="resendingResetLink()"><i class="bi bi-arrow-clockwise spin"></i> Enviando enlace...</span>
              </button>
            </div>
          </section>
        </div>
      </div>
  `,
  styles: [`
      /* Modal "Días no disponibles" (el componente trae su propia tarjeta). */
      .availability-blocks-modal { position: relative; width: min(780px, 100%); max-height: calc(100vh - 48px); overflow-y: auto; border-radius: 16px; box-shadow: 0 24px 64px rgba(2, 8, 23, .35); }
      .availability-blocks-modal app-availability-blocks { display: block; }
      .availability-blocks-modal app-availability-blocks ::ng-deep .ab { padding-right: 3.6rem; }
      .availability-blocks-close { position: absolute; top: 14px; right: 14px; z-index: 1; width: 36px; height: 36px; min-height: 0; display: grid; place-items: center; padding: 0; border: 1px solid var(--ts-border); border-radius: 50%; color: var(--ts-text-muted); background: var(--ts-surface); cursor: pointer; }
      .availability-blocks-close:hover { color: var(--ts-text); border-color: var(--ts-border-strong); }
      @media (max-width: 640px) { .availability-blocks-modal app-availability-blocks ::ng-deep .ab { padding-right: 1rem; padding-top: 3.2rem; } }

      .logo_dashboard {
        display: block;
        width: 100px;      /* o el tamaño que desees */
        height: auto;
        margin: 0 auto 15px auto;
        padding: 0;
        background: transparent;
      }
      

      .dashboard-container {
        min-height: 100vh;
        background: var(--ts-bg-gray-50);
      }
      
      .dashboard-header {
        background: var(--ts-bg);
        color: white;
        padding: 20px 0;
        box-shadow: 0 2px 10px var(--ts-shadow-gray-1000-a30);
      }
      
      .header-content {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .header-content h1 {
        margin: 0;
        font-size: 28px;
        font-weight: 700;
      }
      
      .user-info {
        display: flex;
        align-items: center;
        gap: 20px;
      }
      
      .user-info span {
        font-size: 16px;
        font-weight: 500;
      }
      
      .logout-btn {
        background: white;
        color: var(--ts-bg);
        border: 2px solid white;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 600;
        transition: all 0.3s ease;
      }
      
      .logout-btn:hover {
        background: var(--ts-bg);
        color: white;
      }
      
      .dashboard-nav {
        background: white;
        border-bottom: 2px solid var(--ts-bg);
        padding: 0 20px;
        max-width: 1200px;
        margin: 0 auto;
      }
      
      .nav-btn {
        background: none;
        border: none;
        padding: 16px 24px;
        cursor: pointer;
        font-size: 16px;
        color: var(--ts-text-gray-800);
        border-bottom: 3px solid transparent;
        transition: all 0.3s ease;
        font-weight: 500;
      }
      
      .nav-btn:hover {
        color: var(--ts-bg);
        background: var(--ts-bg-gray-50);
      }
      
      .nav-btn.active {
        color: var(--ts-bg);
        border-bottom-color: var(--ts-bg);
        background: var(--ts-bg-gray-50);
        font-weight: 600;
      }

      .nav-btn-admin {
        color: var(--ts-text-violet-400);
        font-weight: 600;
      }

      .nav-btn-admin.active {
        border-bottom-color: var(--ts-border-violet-400);
        color: var(--ts-text-violet-400);
      }
      
      .dashboard-content {
        max-width: 1200px;
        margin: 0 auto;
        padding: 40px 20px;
      }
      
      .tab-content {
        background: transparent;
        padding: 0;
      }
      
      .salas-container {
        width: 100%;
      }

      .salas-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 30px;
      }
      
      .salas-header h2 {
        margin: 0;
        font-size: 24px;
        font-weight: 700;
        color: var(--ts-text-gray-800);
      }
      
      .btn-primary {
        background: var(--ts-bg);
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 600;
        transition: all 0.3s ease;
      }
      
      .btn-primary:hover {
        background: var(--ts-bg-gray-800);
      }
      
      .empty-state {
        text-align: center;
        padding: 60px 20px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 8px var(--ts-shadow-gray-1000-a10);
      }
      
      .empty-state p {
        font-size: 16px;
        color: var(--ts-text-gray-600);
        margin-bottom: 20px;
      }
      
      .espacios-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 24px;
      }
      
      .espacio-card {
        background: white;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 2px 8px var(--ts-shadow-gray-1000-a10);
        transition: all 0.3s ease;
        cursor: pointer;
      }
      
      .espacio-card:hover {
        box-shadow: 0 4px 16px var(--ts-shadow-gray-1000-a15);
        transform: translateY(-4px);
      }
      
      .card-image {
        position: relative;
        width: 100%;
        height: 250px;
        overflow: hidden;
        background: var(--ts-bg-gray-50);
      }
      
      .card-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      .no-image {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--ts-text-gray-400);
        font-size: 14px;
      }
      
      .status-badge {
        position: absolute;
        top: 12px;
        right: 12px;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        background: white;
      }
      
      .status-badge.active {
        color: var(--ts-text-green-550);
        background: var(--ts-bg-green-50);
      }
      
      .status-badge.unavailable {
        color: var(--ts-text-red-400);
        background: var(--ts-bg-red-50);
      }
      
      .card-content {
        padding: 20px;
      }
      
      .card-content h3 {
        margin: 0 0 8px 0;
        font-size: 20px;
        font-weight: 700;
        color: var(--ts-text-gray-800);
      }
      
      .description {
        color: var(--ts-text-gray-600);
        font-size: 14px;
        margin: 0 0 16px 0;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .card-details {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid var(--ts-border-gray-100);
      }
      
      .detail-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--ts-text-gray-600);
        font-size: 14px;
      }
      
      .detail-item svg {
        color: var(--ts-text-gray-400);
      }
      
      .extras-section {
        margin-bottom: 16px;
      }
      
      .extras-section h4 {
        font-size: 12px;
        font-weight: 600;
        color: var(--ts-text-gray-600);
        margin: 0 0 8px 0;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .extras-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      
      .extra-tag {
        background: var(--ts-bg-gray-50);
        color: var(--ts-text-gray-600);
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;
      }
      
      .card-actions {
        display: flex;
        gap: 10px;
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid var(--ts-border-gray-100);
      }
      
      .btn-secondary {
        flex: 1;
        background: white;
        color: var(--ts-text-gray-800);
        border: 2px solid var(--ts-border-gray-100);
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 600;
        transition: all 0.3s ease;
      }
      
      .btn-secondary:hover {
        border-color: var(--ts-bg);
        background: var(--ts-bg-gray-0-2);
      }
      
      .btn-secondary.danger {
        color: var(--ts-text-red-400);
        border-color: var(--ts-border-red-100);
      }
      
      .btn-secondary.danger:hover {
        border-color: var(--ts-border-red-400);
        background: var(--ts-bg-red-50);
      }

      .business-list {
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 8px var(--ts-shadow-gray-1000-a10);
        overflow: hidden;
      }

      .business-row {
        width: 100%;
        display: grid;
        grid-template-columns: 1.5fr 1.3fr auto;
        gap: 20px;
        align-items: center;
        padding: 18px 20px;
        border: 0;
        border-bottom: 1px solid var(--ts-border-gray-100);
        background: white;
        color: var(--ts-text-gray-800);
        text-align: left;
        cursor: pointer;
        transition: background 0.2s ease;
      }

      .business-row:hover {
        background: var(--ts-bg-gray-0-2);
      }

      .business-row strong,
      .business-row span,
      .business-row small {
        display: block;
      }

      .business-row strong {
        font-size: 16px;
        margin-bottom: 4px;
      }

      .business-row small {
        color: var(--ts-text-gray-600);
        margin-top: 4px;
      }

      .modal-overlay {
        position: fixed;
        inset: 0;
        background: var(--ts-bg-gray-1000-a55);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        z-index: 1000;
      }

      .business-modal {
        width: min(720px, 100%);
        background: white;
        border-radius: 12px;
        box-shadow: 0 20px 60px var(--ts-shadow-gray-1000-a25);
        overflow: hidden;
      }

      .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 24px;
        border-bottom: 1px solid var(--ts-border-gray-100);
      }

      .modal-header h3 {
        margin: 0;
        font-size: 20px;
      }

      .close-btn {
        width: 36px;
        height: 36px;
        border: 0;
        border-radius: 50%;
        background: var(--ts-bg-gray-50);
        cursor: pointer;
        font-size: 22px;
        line-height: 1;
      }

      .business-info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 18px;
        padding: 24px;
      }

      .business-info-grid label {
        display: block;
        margin-bottom: 6px;
        color: var(--ts-text-gray-600);
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
      }

      .business-info-grid span {
        color: var(--ts-text-gray-850);
        font-weight: 600;
        overflow-wrap: anywhere;
      }

      @media (max-width: 720px) {
        .business-row {
          grid-template-columns: 1fr;
        }
      }

      .dashboard-container { background: var(--ts-bg-blue-50); color: var(--ts-text-navy-800); font-family: Inter, Arial, sans-serif; }
      .dashboard-header { padding: 1rem 0; background: linear-gradient(115deg, var(--ts-bg-navy-850-8), var(--ts-bg-navy-750)); box-shadow: 0 5px 20px var(--ts-shadow-navy-850-a22); }
      .header-content { max-width: 1360px; } .header-content h1 { font-size: 1.35rem; letter-spacing: -.025em; }
      .user-info { gap: 1rem; color: var(--ts-text-blue-50-3); } .user-info span { font-size: .87rem; }
      .logout-btn { padding: .52rem .85rem; border: 1px solid var(--ts-border-gray-0-a35); border-radius: 8px; color: var(--ts-text); background: transparent; font-size: .8rem; } .logout-btn:hover { background: var(--ts-bg-gray-0-a14); color: var(--ts-text); }
      .dashboard-nav { display: flex; max-width: none; padding: .55rem max(1rem, calc((100vw - 1320px) / 2)); overflow-x: auto; gap: .3rem; background: var(--ts-text); border-bottom: 1px solid var(--ts-border-blue-100); box-shadow: 0 3px 13px var(--ts-shadow-navy-800-a4); }
      .nav-btn { padding: .58rem .78rem; border: 0; border-radius: 8px; color: var(--ts-text-blue-500); font-size: .84rem; font-weight: 650; white-space: nowrap; } .nav-btn:hover { color: var(--ts-text-blue-550); background: var(--ts-bg-blue-50-2); }
      .nav-btn.active { color: var(--ts-text); border: 0; background: linear-gradient(135deg, var(--ts-bg-blue-500-2), var(--ts-bg-sky-450-2)); box-shadow: 0 4px 10px var(--ts-shadow-blue-500-a20-2); } .nav-btn-admin { color: var(--ts-text-violet-450); } .nav-btn-admin.active { color: var(--ts-text); background: linear-gradient(135deg, var(--ts-bg-violet-450), var(--ts-bg-violet-350)); }
      .dashboard-content { max-width: 1360px; padding: 1.65rem max(1rem, calc((100vw - 1320px) / 2)); }
      .salas-header { margin-bottom: 1.35rem; } .salas-header h2 { color: var(--ts-text-navy-800-2); letter-spacing: -.035em; }
      .btn-primary { border-radius: 9px; background: linear-gradient(135deg, var(--ts-bg-blue-500-3), var(--ts-bg-sky-450-3)); box-shadow: 0 7px 14px var(--ts-shadow-blue-500-a20-3); } .btn-primary:hover { background: var(--ts-bg-blue-600); }
      .empty-state { border: 1px dashed var(--ts-border-sky-150); border-radius: 14px; box-shadow: none; } .espacio-card { border: 1px solid var(--ts-border-blue-100-2); border-radius: 14px; box-shadow: 0 7px 18px var(--ts-shadow-navy-800-a6); } .espacio-card:hover { transform: translateY(-3px); box-shadow: 0 14px 28px var(--ts-shadow-navy-800-a13); }
      .dashboard-header { position:relative; z-index:3; } .dashboard-nav { position:fixed; z-index:2; top:67px; bottom:0; left:0; width:238px; box-sizing:border-box; display:flex; flex-direction:column; align-items:stretch; overflow-y:auto; padding:1rem .7rem; gap:.3rem; border-right:1px solid var(--ts-border-blue-100); border-bottom:0; } .nav-btn { width:100%; text-align:left; padding:.72rem .8rem; } .dashboard-content { max-width:none; min-height:calc(100vh - 67px); margin-left:238px; padding:1.65rem 2rem; } .module-title { margin:0 0 1.5rem; text-align:center; color:var(--ts-text-navy-750); font-size:1.5rem; letter-spacing:-.03em; } .superadmin-menu { display:grid; gap:.2rem; padding:.15rem .25rem .55rem .8rem; border-left:2px solid var(--ts-border-blue-150); margin:.1rem 0 .2rem .55rem; } .superadmin-menu button { border:0; background:transparent; padding:.53rem .55rem; border-radius:7px; text-align:left; color:var(--ts-text-blue-550-2); font-size:.78rem; cursor:pointer; } .superadmin-menu button:hover,.superadmin-menu button.active { color:var(--ts-text-indigo-550); background:var(--ts-bg-indigo-50); font-weight:700; } .espacio-card { overflow:hidden; background:var(--ts-text); } .card-image { min-height:188px; } .card-content { padding:1.15rem; } .card-content h3 { color:var(--ts-text-navy-750); margin:0 0 .5rem; } .card-actions { padding-top:.85rem; border-top:1px solid var(--ts-border-sky-50); } @media(max-width:760px){ .dashboard-nav{position:static;width:auto;flex-direction:row;flex-wrap:wrap;padding:.6rem;overflow:visible}.nav-btn{width:auto}.superadmin-menu{width:100%;margin-left:0}.dashboard-content{margin-left:0;padding:1rem}.module-title{font-size:1.25rem}.dashboard-header{position:static} }
      .dashboard-header { display:none; } .dashboard-nav { top:0; width:272px; padding:1.25rem .85rem; background:var(--ts-text); } .dashboard-content { min-height:100vh; box-sizing:border-box; margin-left:272px; padding:2rem 2.25rem; } .sidebar-brand { display:grid; gap:.42rem; padding:.55rem .7rem 1.2rem; margin-bottom:.45rem; border-bottom:1px solid var(--ts-border-sky-50-2); } .sidebar-brand strong { color:var(--ts-text-navy-750); font-size:1.15rem; letter-spacing:-.025em; } .sidebar-business { color:var(--ts-text-blue-500-2); font-size:1.02rem; font-weight:750; overflow-wrap:anywhere; } .sidebar-footer { margin-top:auto; padding:.9rem .25rem .1rem; border-top:1px solid var(--ts-border-sky-50-2); } .sidebar-footer .logout-btn { width:100%; color:var(--ts-text-red-550); border:1px solid var(--ts-border-red-150); background:var(--ts-bg-red-0); } .sidebar-footer .logout-btn:hover { background:var(--ts-bg-red-50-2); } .logout-confirm { display:grid; place-items:center; z-index:2000; padding:1rem; } .logout-modal { width:min(390px,100%); padding:1.7rem; text-align:center; background:var(--ts-text); border:1px solid var(--ts-border-sky-100-2); border-radius:18px; box-shadow:0 26px 72px var(--ts-shadow-navy-900-a28); } .logout-icon { display:grid; place-items:center; width:42px; height:42px; margin:0 auto .75rem; border-radius:50%; color:var(--ts-text-red-550-2); background:var(--ts-bg-red-50-2); font-size:1.3rem; font-weight:800; } .logout-modal h3 { margin:0; color:var(--ts-text-navy-750); } .logout-modal p { margin:.6rem 0 1.3rem; color:var(--ts-text-blue-500-3); font-size:.88rem; line-height:1.5; } .logout-modal div { display:flex; gap:.65rem; } .logout-modal button { flex:1; padding:.68rem .7rem; border-radius:8px; font-weight:700; cursor:pointer; } .logout-confirm-btn { border:0; color:var(--ts-on-primary); background:var(--ts-bg-red-500-2); } @media(max-width:760px){ .dashboard-nav{width:auto}.dashboard-content{margin-left:0}.sidebar-brand{width:100%}.sidebar-footer{width:100%;margin-top:0}.sidebar-footer .logout-btn{width:auto} }
      .espacios-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(280px,100%),320px));justify-content:start;align-items:start;gap:1rem}.espacio-card{display:flex;min-width:0;max-width:320px;flex-direction:column;border:1px solid var(--ts-border-sky-100-3);border-radius:15px;box-shadow:0 8px 20px var(--ts-shadow-navy-750-a8);transition:transform .18s ease,box-shadow .18s ease}.espacio-card:hover{transform:translateY(-3px);box-shadow:0 14px 28px var(--ts-shadow-navy-750-a14)}.card-image{position:relative;min-height:142px;background:linear-gradient(135deg,var(--ts-text),var(--ts-bg-blue-0))}.card-image img{width:100%;height:142px;object-fit:cover}.no-image{display:grid;place-items:center;height:142px;color:var(--ts-text-blue-450);font-size:.78rem;background:radial-gradient(circle at 18% 25%,var(--ts-text) 0 3px,transparent 4px),linear-gradient(135deg,var(--ts-bg-blue-50-3),var(--ts-bg-blue-0-2))}.card-image .status-badge{position:absolute;top:.65rem;right:.65rem;padding:.3rem .52rem;border:1px solid var(--ts-border-gray-0-a80);box-shadow:0 3px 10px var(--ts-shadow-navy-700-a12);font-size:.65rem}.card-content{display:flex;flex:1;flex-direction:column;padding:.85rem}.card-content h3{display:-webkit-box;overflow:hidden;margin:0 0 .35rem;font-size:.92rem;line-height:1.32;-webkit-box-orient:vertical;-webkit-line-clamp:2}.description{display:-webkit-box;overflow:hidden;min-height:2.72em;margin:0;color:var(--ts-text-blue-500-4);font-size:.73rem;line-height:1.36;-webkit-box-orient:vertical;-webkit-line-clamp:2;text-overflow:ellipsis}.card-details{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.42rem;margin:.7rem 0}.detail-item{min-width:0;padding:.42rem;border-radius:8px;color:var(--ts-text-blue-600);background:var(--ts-bg-sky-50-3);font-size:.68rem}.detail-item span{min-width:0;overflow:hidden;text-overflow:ellipsis}.category-item{grid-column:1/-1;display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center}.category-item small{padding:.15rem .35rem;color:var(--ts-text-blue-150);border-radius:999px;background:var(--ts-bg-blue-400-a15);font-size:.56rem;font-weight:800;white-space:nowrap}.detail-item:last-child{grid-column:1/-1}.extras-section{margin:.1rem 0 .65rem!important;padding-top:.55rem!important}.extras-section h4{margin-bottom:.35rem!important;font-size:.61rem!important}.extra-tag{padding:.2rem .38rem!important;font-size:.56rem!important}.card-actions{display:flex;flex-wrap:wrap;gap:.4rem;margin-top:auto;padding-top:.65rem}.card-actions .btn-secondary,.card-actions .btn-featured{flex:0 0 auto;width:36px;height:36px;min-height:36px;padding:0;display:grid;place-items:center;border-radius:9px;font-size:.98rem;font-weight:750}.delete-space-modal .logout-icon{color:var(--ts-text-red-250-4)}
      .forced-password .logout-modal{display:grid;gap:.65rem;text-align:left}.forced-password input{min-height:42px;padding:.6rem;border:1px solid var(--ts-border-sky-150-2);border-radius:8px}.forced-password small{color:var(--ts-text-sky-500-2)}.password-error{color:var(--ts-danger-text)!important;background:var(--ts-danger-soft);border:1px solid var(--ts-danger-soft-2);padding:.5rem;border-radius:7px;font-weight:650}
      .forced-password{z-index:10000!important;padding:1rem;background:var(--ts-bg-navy-950-a88);backdrop-filter:blur(10px)}
      .forced-password-card{width:min(560px,100%);max-height:calc(100dvh - 24px);box-sizing:border-box;overflow:auto;padding:1.35rem;color:var(--ts-text);border:1px solid var(--ts-border-navy-650-3);border-radius:22px;background:radial-gradient(circle at 92% 4%,var(--ts-bg-blue-400-a20-2),transparent 31%),var(--ts-bg-navy-850-9);box-shadow:0 28px 90px var(--ts-shadow-gray-1000-a55)}
      .forced-password-hero{display:grid;grid-template-columns:54px minmax(0,1fr);align-items:center;gap:.85rem;padding-bottom:1rem;border-bottom:1px solid var(--ts-border-navy-700-2)}.forced-password-icon{display:grid;place-items:center;width:52px;height:52px;color:var(--ts-text-blue-200-3);border:1px solid var(--ts-border-blue-350-a40);border-radius:15px;background:linear-gradient(145deg,var(--ts-bg-blue-450-a25),var(--ts-bg-indigo-350-a17));font-size:1.3rem}.forced-password-hero small{display:block;color:var(--ts-text-blue-250-4);font-size:.6rem;font-weight:900;letter-spacing:.12em}.forced-password-hero h3{margin:.22rem 0;color:var(--ts-text);font-size:1.3rem}.forced-password-hero p{margin:0;color:var(--ts-text-sky-300-5);font-size:.75rem;line-height:1.5}
      .forced-password-notice{display:flex;align-items:flex-start;gap:.55rem;margin:1rem 0;padding:.7rem;color:var(--ts-text-sky-150-8);border:1px solid var(--ts-border-blue-400-a25-2);border-radius:10px;background:var(--ts-bg-sky-600-a12);font-size:.7rem;line-height:1.45}.forced-password-notice i{color:var(--ts-text-sky-300-6)}
      .forced-password-email{display:flex;align-items:center;gap:.5rem;margin:0 0 1rem;padding:.7rem .8rem;border:1px solid var(--ts-border-blue-300-a24);border-radius:10px;background:var(--ts-bg-navy-750-a28);color:var(--ts-text-sky-50-3);font-size:.72rem;font-weight:700}.forced-password-email i{color:var(--ts-text-blue-250-5)}
      .forced-password-actions{display:flex;align-items:center;justify-content:flex-end;gap:.7rem;flex-wrap:wrap;margin-top:.25rem}
      .forced-password-secondary,.forced-password-submit{display:inline-flex;align-items:center;justify-content:center;gap:.55rem;min-height:42px;padding:.7rem 1rem;border-radius:11px;font-weight:800;cursor:pointer;white-space:nowrap;width:auto!important;max-width:none!important;flex:0 0 auto}
      .forced-password-secondary{color:var(--ts-text-blue-50-4);border:1px solid var(--ts-border-sky-300-a38);background:var(--ts-bg-navy-850-a95)}
      .forced-password-submit{color:var(--ts-text);border:0;background:linear-gradient(135deg,var(--ts-bg-blue-450-3),var(--ts-bg-indigo-350-3));box-shadow:0 10px 24px var(--ts-shadow-blue-450-a23)}.forced-password-submit:disabled{opacity:.42;cursor:not-allowed;box-shadow:none}.spin{display:inline-block;animation:passwordSpin .8s linear infinite}@keyframes passwordSpin{to{transform:rotate(360deg)}}
      @media(max-width:640px){.forced-password{padding:.55rem}.forced-password-card{max-height:calc(100dvh - 12px);padding:1rem;border-radius:17px}.forced-password-hero{grid-template-columns:44px 1fr;gap:.65rem}.forced-password-icon{width:42px;height:42px}.forced-password-hero h3{font-size:1.05rem}.forced-password-notice,.forced-password-email{margin:.75rem 0;padding:.6rem}.forced-password-actions{justify-content:stretch}.forced-password-secondary,.forced-password-submit{flex:1 1 180px;width:auto!important}}
      .espacio-card .card-image{height:142px;min-height:142px;overflow:hidden}.espacio-card .card-image img{display:block;width:100%;height:100%;object-fit:cover;object-position:center}.espacio-card .no-image{height:100%}
      .espacio-card .no-image{position:relative;isolation:isolate;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.28rem;overflow:hidden;padding:1rem;text-align:center;background:linear-gradient(145deg,color-mix(in srgb,var(--ts-primary) 8%,var(--ts-surface)),var(--ts-surface-2))}
      .espacio-card .no-image-art{position:relative;display:grid;place-items:center;width:44px;height:44px;margin-bottom:.1rem;color:var(--ts-text-blue-300-4);border:1px solid color-mix(in srgb,var(--ts-primary) 24%,var(--ts-border));border-radius:14px;background:color-mix(in srgb,var(--ts-primary) 10%,var(--ts-surface));font-size:1.05rem}
      .espacio-card .no-image strong{position:relative;z-index:1;color:var(--ts-text);font-size:.72rem}.espacio-card .no-image small{position:relative;z-index:1;max-width:28ch;color:var(--ts-text-muted);font-size:.58rem;line-height:1.35}
      /* Anillos ESTÁTICOS (marco decorativo). Bug real reportado: con la
         animación continua de "respiración", cada anillo -- y el ícono encima
         de ellos -- iba en su propia capa gráfica; al hacer scroll con rueda o
         touchpad (Edge), esas capas se veían despegadas, "flotando" sobre la
         tarjeta. Medido: 15 capas extra en Gestión de salas, 0 sin animación. */
      .no-image-ring{position:absolute;z-index:-1;width:56px;height:56px;border:1px solid color-mix(in srgb,var(--ts-primary) 22%,transparent);border-radius:17px;opacity:.38}.no-image-ring.ring-two{width:66px;height:66px;border-radius:20px;opacity:.18}
      .badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 4px;margin-left:6px;border-radius:10px;background:var(--ts-bg-red-500-2);color:var(--ts-on-primary);font-size:.65rem;font-weight:750;line-height:1;white-space:nowrap;vertical-align:middle}
      .superadmin-menu .badge{background:var(--ts-bg-violet-450-2)}
      .dashboard-container{min-height:100vh!important;background:var(--ts-bg)!important}
      .dashboard-nav{display:none}
      .logo_dashboard{width:112px;margin:0}
      .sidebar-brand{display:flex;align-items:center;min-height:48px;padding:.25rem .45rem 1rem;margin:0 0 .75rem;border-bottom:1px solid var(--ts-border)}
      .sidebar-business{display:none}
      .sidebar-profile{display:grid;grid-template-columns:38px minmax(0,1fr);gap:.65rem;align-items:center;padding:.65rem;margin-bottom:.85rem;border:1px solid var(--ts-border);border-radius:12px;background:var(--ts-header-wash-2)}
      .profile-avatar{display:grid;place-items:center;width:38px;height:38px;border:1px solid var(--ts-border-blue-350-a38);border-radius:50%;color:var(--ts-text);background:linear-gradient(135deg,var(--ts-bg-blue-500-4),var(--ts-bg-indigo-400));font-size:.76rem;font-weight:800}
      .sidebar-profile strong,.sidebar-profile small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.sidebar-profile strong{color:var(--ts-text);font-size:.8rem}.sidebar-profile small{margin-top:.18rem;color:var(--ts-text-muted);font-size:.68rem}
      .nav-section-label{padding:.9rem .55rem .35rem;color:var(--ts-text-sky-500-3);font-size:.62rem;font-weight:800;letter-spacing:.09em;text-transform:uppercase}
      .nav-btn{display:flex;align-items:center;gap:.72rem;width:100%;min-height:38px;padding:.55rem .65rem;border:1px solid transparent;border-radius:9px;color:var(--ts-text-sky-250);font-size:.76rem;font-weight:550;text-align:left}
      .nav-btn i{width:17px;color:var(--ts-text-sky-400-2);font-size:.91rem;text-align:center}.nav-btn span:not(.badge){min-width:0;overflow:hidden;text-overflow:ellipsis}
      .nav-btn:hover{color:var(--ts-text);background:var(--ts-bg-blue-400-a13);border-color:var(--ts-border-blue-400-a19)}.nav-btn:hover i{color:var(--ts-text-blue-250-6)}
      .nav-btn.active{color:var(--ts-text);background:linear-gradient(135deg,var(--ts-bg-blue-400-a88),var(--ts-bg-blue-450-a86));border-color:var(--ts-border-blue-300-a42);box-shadow:0 7px 20px var(--ts-shadow-blue-550-a25)}.nav-btn.active i{color:var(--ts-text)}
      .nav-btn-admin{color:var(--ts-text-indigo-150)}.nav-btn-admin.active{background:linear-gradient(135deg,var(--ts-bg-indigo-500),var(--ts-bg-violet-450-3))}
      .superadmin-menu{gap:.18rem;padding:.35rem 0 .45rem 1rem;margin:0;border:0}.superadmin-menu button{position:relative;display:grid;grid-template-columns:20px 1fr auto;align-items:center;gap:.42rem;min-height:35px;padding:.45rem .6rem;border:1px solid transparent;color:var(--ts-text-sky-350);font-size:.7rem}.superadmin-menu button::before{display:none}.superadmin-menu button i{display:grid;place-items:center;width:20px;height:20px;border-radius:6px;color:var(--ts-text-sky-400-3);background:var(--ts-bg-sky-450-a10);font-size:.72rem}.superadmin-menu button:hover,.superadmin-menu button.active{color:var(--ts-text-on-accent-6);border-color:var(--ts-border-indigo-300-a20);background:linear-gradient(90deg,var(--ts-bg-indigo-350-a18),var(--ts-bg-blue-400-a10))}.superadmin-menu button.active i{color:var(--ts-text);background:linear-gradient(135deg,var(--ts-bg-blue-350-2),var(--ts-bg-indigo-350-4));box-shadow:0 4px 10px var(--ts-shadow-blue-400-a20-2)}
      .sidebar-footer{margin-top:auto;padding:.8rem 0 0;border-top:1px solid var(--ts-border)}.sidebar-footer .logout-btn{display:flex;align-items:center;justify-content:center;gap:.55rem;width:100%;min-height:40px;color:var(--ts-text-red-250-5);border:1px solid var(--ts-border-red-350-a22);background:var(--ts-bg-red-350-a10)}
      .dashboard-content{position:relative;min-width:0;min-height:calc(100dvh - 112px);margin-left:0;padding:0 1.35rem 2rem;background:radial-gradient(circle at 70% -10%,var(--ts-bg-blue-500-a13),transparent 31rem),var(--ts-bg)!important;overflow-x:hidden}
      .dashboard-content.module-loading>:not(.page-heading):not(.module-data-skeleton){visibility:hidden!important;pointer-events:none!important}
      .module-data-skeleton{position:relative;z-index:18;display:block;min-height:calc(100dvh - 220px);padding:0 0 1rem;background:var(--ts-bg)}
      .workspace-header{position:sticky;top:0;z-index:20;display:flex;align-items:center;justify-content:space-between;gap:1rem;height:60px;margin:0 -1.35rem;padding:0 1.35rem;border-bottom:1px solid var(--ts-border);background:var(--ts-bg-navy-900-a88);backdrop-filter:blur(16px)}
      .workspace-identity{display:flex;align-items:center;gap:.6rem;min-width:0;height:40px;white-space:nowrap}.workspace-identity .profile-avatar{flex:0 0 34px;width:34px;height:34px}.workspace-business,.workspace-user{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.workspace-business{max-width:250px;color:var(--ts-text-blue-0-2);font-size:.8rem}.workspace-user{max-width:210px;color:var(--ts-text-sky-200-4);font-size:.72rem}.identity-separator{flex:0 0 1px;width:1px;height:20px;background:var(--ts-border)}.workspace-role{flex:0 0 auto;padding:.28rem .5rem;color:var(--ts-text-blue-250-7);border:1px solid var(--ts-border-blue-350-a28);border-radius:999px;background:var(--ts-bg-blue-400-a16);font-size:.6rem;font-weight:750}
      .header-actions{display:flex;align-items:center;gap:.35rem}.header-actions>button{position:relative;display:grid;place-items:center;width:34px;height:34px;padding:0;color:var(--ts-text-sky-300-7);border:1px solid transparent;border-radius:8px;background:transparent}.header-actions>button:hover{color:var(--ts-text);border-color:var(--ts-border);background:var(--ts-surface)}
      .notification-dot{position:absolute;top:7px;right:7px;width:6px;height:6px;border:1px solid var(--ts-border-navy-900);border-radius:50%;background:var(--ts-bg-red-400-2)}.current-date{display:flex;align-items:center;gap:.5rem;height:34px;margin-left:.35rem;padding:0 .7rem;color:var(--ts-text-sky-150-9);border:1px solid var(--ts-border);border-radius:8px;background:var(--ts-surface);font-size:.7rem}
      .page-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:1rem;padding:1.15rem 0 1rem}.page-heading h1{margin:.2rem 0 .15rem;color:var(--ts-text-blue-0-3);font-family:"Plus Jakarta Sans",Inter,sans-serif;font-size:1.35rem;letter-spacing:-.035em}.page-heading p{margin:0;color:var(--ts-text-muted);font-size:.75rem}.breadcrumb{color:var(--ts-text-sky-500-4);font-size:.62rem}.page-heading .btn-primary{display:flex;align-items:center;gap:.45rem;min-height:38px;padding:0 .8rem;font-size:.72rem}
      .module-title{display:none}
      .control-overview{display:grid;gap:1rem}.welcome-panel{display:flex;align-items:center;justify-content:space-between;min-height:126px;padding:1.25rem 1.4rem;overflow:hidden;border:1px solid var(--ts-border);border-radius:16px;background:linear-gradient(125deg,var(--ts-bg-navy-850-a94),var(--ts-bg-navy-900-a94));box-shadow:var(--ts-shadow-sm)}
      .welcome-panel h2{margin:.25rem 0;color:var(--ts-text);font-family:"Plus Jakarta Sans",Inter,sans-serif;font-size:1.4rem}.welcome-panel p{margin:0;color:var(--ts-text-muted);font-size:.78rem}.welcome-mark{display:grid;place-items:center;width:64px;height:64px;border:1px solid var(--ts-border-blue-300-a28);border-radius:18px;color:var(--ts-text-blue-250-8);background:linear-gradient(145deg,var(--ts-primary-soft-14),var(--ts-bg-indigo-350-a13));font-size:1.7rem}
      .eyebrow{color:var(--ts-text-blue-300-3);font-size:.58rem;font-weight:800;letter-spacing:.09em}
      .overview-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.8rem}.overview-kpi{display:grid;grid-template-columns:42px 1fr;gap:.12rem .7rem;min-height:112px;padding:.85rem;text-align:left;color:var(--ts-text);border:1px solid var(--ts-border);border-radius:14px;background:var(--ts-surface);box-shadow:var(--ts-shadow-sm);cursor:pointer;transition:transform var(--ts-transition),border-color var(--ts-transition),background var(--ts-transition)}
      .overview-kpi:hover{transform:translateY(-3px);border-color:var(--ts-border-blue-400-a40);background:var(--ts-surface-hover)}.kpi-icon{grid-row:1/4;display:grid;place-items:center;width:42px;height:42px;border-radius:12px;font-size:1.05rem}.overview-kpi>span:not(.kpi-icon){color:var(--ts-text-sky-200-5);font-size:.7rem}.overview-kpi strong{overflow:hidden;color:var(--ts-text);font-size:1.05rem;text-overflow:ellipsis;white-space:nowrap}.overview-kpi small{color:var(--ts-text-sky-450-2);font-size:.64rem}.overview-kpi.blue .kpi-icon{color:var(--ts-text-blue-300-4);background:var(--ts-bg-blue-400-a24)}.overview-kpi.indigo .kpi-icon{color:var(--ts-text-indigo-200);background:var(--ts-bg-indigo-350-a15)}.overview-kpi.green .kpi-icon{color:var(--ts-text-green-350-2);background:var(--ts-bg-green-500-a22)}.overview-kpi.gold .kpi-icon{color:var(--ts-text-amber-350);background:var(--ts-bg-orange-450-a22)}
      .overview-grid{display:grid;grid-template-columns:1.15fr .85fr;gap:1rem}.overview-card{padding:1rem;border:1px solid var(--ts-border);border-radius:14px;background:var(--ts-surface);box-shadow:var(--ts-shadow-sm)}.overview-card header{display:flex;align-items:center;justify-content:space-between;margin-bottom:.85rem}.overview-card h3{margin:.2rem 0 0;color:var(--ts-text);font-size:.92rem}.quick-actions{display:grid;grid-template-columns:repeat(3,1fr);gap:.6rem}.quick-actions button{display:flex;gap:.65rem;align-items:center;min-height:66px;padding:.7rem;text-align:left;color:var(--ts-text-on-accent-7);border:1px solid var(--ts-border);border-radius:10px;background:var(--ts-header-wash-2)}.quick-actions button:hover{border-color:var(--ts-border-blue-400-a38);background:var(--ts-primary-soft-07)}.quick-actions i{color:var(--ts-eyebrow);font-size:1rem}.quick-actions strong,.quick-actions small{display:block}.quick-actions strong{font-size:.7rem}.quick-actions small{margin-top:.18rem;color:var(--ts-text-sky-450-3);font-size:.6rem}
      .health-pill{display:flex;align-items:center;gap:.35rem;padding:.3rem .5rem;color:var(--ts-text-green-350-3);border-radius:999px;background:var(--ts-bg-green-500-a14);font-size:.62rem}.health-pill i{width:6px;height:6px;border-radius:50%;background:var(--ts-success);box-shadow:0 0 8px var(--ts-success)}.system-status{display:grid;gap:.55rem;margin:0;padding:0;list-style:none}.system-status li{display:flex;justify-content:space-between;gap:1rem;padding:.6rem .7rem;color:var(--ts-text-muted);border-radius:9px;background:var(--ts-header-wash-2);font-size:.67rem}.system-status strong{max-width:55%;overflow:hidden;color:var(--ts-text-on-accent-2);text-overflow:ellipsis;white-space:nowrap}
      .space-search{display:grid;grid-template-columns:auto minmax(180px,1fr) auto auto;align-items:center;gap:.55rem;margin:0 0 1rem;padding:.65rem .8rem;border:1px solid var(--ts-border);border-radius:12px;background:var(--ts-surface)}.space-search i{color:var(--ts-text-blue-250-6)}.space-search input{min-width:0;padding:.58rem .7rem;color:var(--ts-text-soft-2);border:1px solid var(--ts-border-sky-400-a28);border-radius:8px;background:var(--ts-bg-elevated);outline:none}.space-search input:focus{border-color:var(--ts-border-blue-400-3);box-shadow:0 0 0 3px var(--ts-shadow-blue-400-a12)}.space-search button{display:flex;align-items:center;justify-content:center;gap:.4rem;min-width:96px;padding:.58rem .75rem;color:var(--ts-text);border:0;border-radius:8px;background:var(--ts-bg-blue-500-3);cursor:pointer}.space-search button i{color:inherit}.space-search button:disabled{cursor:wait;opacity:.72}.space-search button .bi-arrow-repeat{animation:searchSpin .8s linear infinite}.space-search button.clear{min-width:auto;color:var(--ts-text-sky-250-2);background:transparent}@keyframes searchSpin{to{transform:rotate(360deg)}}@media(max-width:620px){.space-search{grid-template-columns:auto 1fr auto}.space-search button.clear{grid-column:2/-1}}
      .espacio-card{color:var(--ts-text);background:var(--ts-surface)!important;border-color:var(--ts-border)!important}.card-content h3{color:var(--ts-text)}.description,.detail-item{color:var(--ts-text-sky-150-10)}.detail-item{background:var(--ts-bg-gray-0-a5)}.category-item{color:var(--ts-text-blue-200-4);background:var(--ts-bg-blue-400-a19)}.card-actions{border-color:var(--ts-border)}
      .spaces-heading-actions{display:flex;align-items:stretch;justify-content:flex-end;gap:.65rem}.spaces-heading-actions>.btn-primary{white-space:nowrap}.page-size-control{display:inline-flex;align-items:center;gap:.45rem;color:var(--ts-text-blue-400);font-size:.72rem;font-weight:600;white-space:nowrap;height:100%}.page-size-control select{height:32px;min-height:32px;min-width:74px;padding:0 .35rem 0 .55rem;color:var(--ts-text-blue-50-5);background:var(--ts-bg-navy-850-10);border:1px solid var(--ts-border-navy-700-3);border-radius:7px;font-size:.72rem;font-weight:500;cursor:pointer;appearance:auto;-webkit-appearance:auto;box-shadow:none}.page-size-control select:focus{outline:none;border-color:var(--ts-border-blue-450);box-shadow:0 0 0 2px var(--ts-shadow-blue-450-a25)}.featured-listing-summary{display:flex;align-items:center;gap:.62rem;min-width:220px;padding:.48rem .7rem;text-align:left;color:var(--ts-text-on-accent-8);border:1px solid var(--ts-border-indigo-300-a42);border-radius:11px;background:linear-gradient(135deg,var(--ts-bg-indigo-500-a20),var(--ts-bg-blue-500-a13-2));cursor:default}.featured-listing-summary.is-upgrade{cursor:pointer;transition:transform .2s ease,border-color .2s ease,box-shadow .2s ease}.featured-listing-summary.is-upgrade:hover{transform:translateY(-2px);border-color:var(--ts-border-indigo-200-a72);box-shadow:0 8px 24px var(--ts-shadow-indigo-550-a20)}.featured-summary-icon{display:grid;place-items:center;flex:0 0 34px;width:34px;height:34px;color:var(--ts-text-amber-150);border:1px solid var(--ts-border-amber-250-a25);border-radius:10px;background:linear-gradient(145deg,var(--ts-bg-amber-350-a18),var(--ts-bg-indigo-400-a20))}.featured-summary-copy{display:grid;gap:.08rem;min-width:0;line-height:1.15}.featured-summary-copy strong{color:var(--ts-text-on-accent-9);font-size:.72rem}.featured-summary-copy small{color:var(--ts-text-indigo-250);font-size:.61rem;font-weight:700}.featured-summary-arrow{margin-left:auto;color:var(--ts-text-indigo-150-2);font-size:.72rem}.featured-badge{position:absolute;left:.8rem;top:.8rem;display:inline-flex;align-items:center;gap:.35rem;padding:.35rem .58rem;border:1px solid var(--ts-border-amber-300-a65);border-radius:999px;color:var(--ts-text-amber-150-2);background:var(--ts-bg-amber-800-a86);font-size:.7rem;font-weight:800;backdrop-filter:blur(8px)}.card-actions .btn-featured{border:1px solid var(--ts-border-indigo-300-a45);color:var(--ts-text-indigo-150-3);background:var(--ts-bg-indigo-450-a11);cursor:pointer}.card-actions .btn-featured.active{color:var(--ts-text-amber-150-2);border-color:var(--ts-border-amber-350-a50);background:var(--ts-bg-amber-750-a19)}.card-actions .btn-featured:disabled{cursor:not-allowed;opacity:.48}
      .featured-upgrade-overlay{padding:1rem;backdrop-filter:blur(9px);background:var(--ts-bg-navy-950-a76)}.featured-upgrade-modal{position:relative;width:min(560px,100%);max-height:calc(100dvh - 2rem);overflow:auto;padding:1.55rem;border:1px solid var(--ts-border-indigo-250-a50);border-radius:22px;color:var(--ts-text-on-accent-10);background:radial-gradient(circle at 85% 0%,var(--ts-bg-indigo-350-a25),transparent 35%),radial-gradient(circle at 0% 90%,var(--ts-bg-blue-400-a16-2),transparent 38%),var(--ts-bg-navy-850-11);box-shadow:0 28px 80px var(--ts-shadow-gray-1000-a48),0 0 36px var(--ts-shadow-indigo-400-a14)}.featured-upgrade-modal::before{content:"";position:absolute;inset:0;pointer-events:none;border-radius:inherit;background:linear-gradient(120deg,transparent 22%,var(--ts-bg-gray-0-a6) 38%,transparent 52%);background-size:220% 100%;animation:premiumSheen 5s ease-in-out infinite}.featured-upgrade-close{position:absolute;z-index:2;top:.85rem;right:.85rem;display:grid;place-items:center;width:34px;height:34px;color:var(--ts-text-blue-200-5);border:1px solid var(--ts-border-sky-300-a24);border-radius:50%;background:var(--ts-bg-navy-900-a68);cursor:pointer}.featured-upgrade-hero{position:relative;z-index:1;text-align:center}.featured-upgrade-icon{display:grid;place-items:center;width:64px;height:64px;margin:0 auto .75rem;color:var(--ts-text-amber-150-3);border:1px solid var(--ts-border-amber-300-a40);border-radius:19px;background:linear-gradient(145deg,var(--ts-bg-amber-400-a25),var(--ts-bg-indigo-400-a34));box-shadow:0 0 26px var(--ts-shadow-amber-350-a13);font-size:1.65rem}.featured-upgrade-label{display:inline-flex;align-items:center;gap:.35rem;padding:.28rem .55rem;color:var(--ts-text-amber-200-2);border:1px solid var(--ts-border-amber-300-a27);border-radius:999px;background:var(--ts-bg-amber-600-a12);font-size:.61rem;font-weight:850;letter-spacing:.06em;text-transform:uppercase}.featured-upgrade-hero h2{margin:.72rem auto .48rem;max-width:420px;color:var(--ts-text);font-family:"Plus Jakarta Sans",Inter,sans-serif;font-size:1.55rem;line-height:1.18}.featured-upgrade-hero p{max-width:450px;margin:0 auto;color:var(--ts-text-sky-250-3);font-size:.78rem;line-height:1.65}.featured-upgrade-benefits{position:relative;z-index:1;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.6rem;margin:1.15rem 0}.featured-upgrade-benefits>span{display:grid;grid-template-columns:auto 1fr;gap:.16rem .42rem;align-content:start;min-width:0;padding:.72rem;border:1px solid var(--ts-border-blue-350-a20);border-radius:12px;background:var(--ts-wash-035)}.featured-upgrade-benefits i{grid-row:1/3;color:var(--ts-text-indigo-250-2)}.featured-upgrade-benefits strong{font-size:.66rem}.featured-upgrade-benefits small{color:var(--ts-text-sky-400-4);font-size:.57rem;line-height:1.4}.featured-upgrade-actions{position:relative;z-index:1;display:flex;justify-content:flex-end;gap:.6rem;padding-top:.15rem}.featured-upgrade-actions button{min-height:42px;padding:.65rem .9rem;border-radius:10px;font-weight:800;cursor:pointer}.featured-upgrade-secondary{color:var(--ts-text-sky-250-4);border:1px solid var(--ts-border-sky-350-a24);background:transparent}.featured-upgrade-primary{display:flex;align-items:center;justify-content:center;gap:.55rem;color:var(--ts-text);border:0;background:linear-gradient(110deg,var(--ts-bg-blue-400-2),var(--ts-bg-indigo-350-5));box-shadow:0 10px 25px var(--ts-shadow-blue-400-a27)}.upgrade-spark{position:absolute;z-index:0;width:5px;height:5px;border-radius:50%;background:var(--ts-bg-amber-150);box-shadow:0 0 10px 3px var(--ts-shadow-amber-300-a35);animation:premiumSpark 3.4s ease-in-out infinite}.spark-one{top:15%;left:10%}.spark-two{top:24%;right:12%;animation-delay:1.1s}.spark-three{bottom:19%;left:16%;animation-delay:2.1s}@keyframes premiumSheen{0%,55%{background-position:130% 0}100%{background-position:-80% 0}}@keyframes premiumSpark{0%,100%{opacity:.22;transform:scale(.6)}50%{opacity:1;transform:scale(1.25)}}@media(prefers-reduced-motion:reduce){.featured-upgrade-modal::before,.upgrade-spark{animation:none}.featured-listing-summary.is-upgrade{transition:none}}
      .critical-label{display:inline-flex;padding:.25rem .48rem;color:var(--ts-text-orange-250-2);border:1px solid var(--ts-border-orange-350-a25-2);border-radius:999px;background:var(--ts-bg-orange-450-a8);font-size:.57rem;font-weight:850;letter-spacing:.07em;text-transform:uppercase;margin:-.15rem 0 .65rem;color:var(--ts-text-red-200-2);border-color:var(--ts-border-red-300-a24);background:var(--ts-bg-red-450-a8)}.critical-note{display:flex;align-items:center;gap:.45rem;padding:.6rem;border-radius:10px;font-size:.65rem;margin:.8rem 0;color:var(--ts-text-red-150-2);background:var(--ts-bg-red-450-a8-2)}.critical-action-modal{border-color:var(--ts-border-red-300-a38)!important;background:radial-gradient(circle at 100% 0,var(--ts-bg-red-450-a13),transparent 40%),var(--ts-surface)!important}.critical-action-modal .logout-icon{background:var(--ts-bg-red-400-a12)!important}.delete-space-btn{display:flex;align-items:center;justify-content:center;gap:.5rem}.delete-space-btn:disabled{opacity:.62;cursor:wait}.delete-space-btn .spin{display:inline-block;animation:passwordSpin .8s linear infinite}
      .card-image .status-badge{color:var(--ts-text);border-color:var(--ts-border-gray-0-a35);text-shadow:0 1px 2px var(--ts-shadow-gray-1000-a45);backdrop-filter:blur(8px)}
      .card-image .status-badge.active{color:var(--ts-text-on-accent-11)!important;background:var(--ts-bg-green-750-a92)!important}
      .card-image .status-badge.unavailable{color:var(--ts-text-on-accent-12)!important;background:var(--ts-bg-red-600-a94)!important}
      /* Visibilidad real del espacio (público / privado / privado por negocio):
         chip sobre el título -- encima de la foto tapaba el aviso de "Sin
         fotografía" en las tarjetas sin imagen. */
      .visibility-chip{display:inline-flex;width:fit-content;max-width:100%;align-self:flex-start;justify-self:start;align-items:center;gap:.35rem;margin:0 0 .5rem;padding:.22rem .6rem;border:1px solid var(--ts-border-strong);border-radius:999px;color:var(--ts-text-muted);background:transparent;font-size:.68rem;font-weight:750;line-height:1.3}
      .visibility-chip.is-private{border-color:var(--ts-border-indigo-300-a45);color:var(--ts-text-indigo-150-3);background:var(--ts-bg-indigo-450-a11)}
      .logout-modal{color:var(--ts-text);background:var(--ts-surface)!important;border-color:var(--ts-border)!important}.logout-modal h3{color:var(--ts-text)}.logout-modal p{color:var(--ts-text-muted)}
      .espacios-grid{grid-auto-rows:1fr!important;align-items:stretch!important}.espacio-card{height:100%!important}.extras-section{min-height:42px}.extras-section.is-empty{opacity:.55}.space-upgrade-options{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.7rem;margin:1rem 0}.space-upgrade-options article{display:grid;gap:.25rem;padding:.9rem;border:1px solid var(--ts-border-blue-300-a30);border-radius:12px;background:var(--ts-bg-navy-750-a58)}.space-upgrade-options span,.space-upgrade-options small{color:var(--ts-text-sky-250-5);font-size:.72rem}.space-upgrade-options strong{color:var(--ts-text)}.custom-space-quote{display:flex;gap:.8rem;align-items:flex-start;padding:.9rem;border:1px solid var(--ts-border-teal-400-a35);border-radius:12px;background:var(--ts-bg-teal-800-a18)}.custom-space-quote>i{color:var(--ts-text-teal-400);font-size:1.35rem}.custom-space-quote p{margin:.25rem 0;color:var(--ts-text-sky-250-6);font-size:.75rem}.custom-space-quote a{color:var(--ts-text-sky-250-7);font-size:.75rem;font-weight:800}.space-limit-modal{max-width:650px}
      .espacios-grid{grid-template-columns:repeat(auto-fill,minmax(min(300px,100%),1fr));justify-content:stretch;align-items:stretch;gap:1rem}.espacio-card{width:100%;max-width:none}
      @media(max-width:1100px){.overview-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.overview-grid{grid-template-columns:1fr}.quick-actions{grid-template-columns:repeat(3,1fr)}}
      @media(max-width:900px){.dashboard-content{margin-left:0;padding-inline:1rem}.overview-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.overview-grid{grid-template-columns:1fr}.quick-actions{grid-template-columns:repeat(2,minmax(0,1fr))}.page-heading,.welcome-panel,.overview-card,.overview-kpi{min-width:0;max-width:100%}}
      @media(max-width:760px){.dashboard-nav{display:none}.dashboard-content{margin-left:0;padding:0 .85rem 1.25rem;min-height:calc(100dvh - 92px)}.workspace-header{margin:0 -.85rem;padding:0 .85rem}.header-actions>button:nth-of-type(2),.header-actions>button:nth-of-type(3),.current-date{display:none}.workspace-identity{max-width:calc(100vw - 78px);gap:.42rem}.workspace-business{max-width:34vw}.workspace-user,.identity-separator{display:none}.workspace-role{max-width:25vw;overflow:hidden;text-overflow:ellipsis}.page-heading{align-items:flex-start;flex-direction:column}.spaces-heading-actions{width:100%;justify-content:stretch}.spaces-heading-actions>*{flex:1 1 0}.featured-listing-summary{min-width:0}.overview-kpis,.quick-actions{grid-template-columns:repeat(2,minmax(0,1fr))}.overview-grid{grid-template-columns:1fr}.welcome-panel{padding:1rem}.welcome-panel h2{font-size:1.35rem}.welcome-mark{display:none}.module-data-skeleton{min-height:calc(100dvh - 190px)}.salas-grid,.stats-grid,.charts-grid{grid-template-columns:repeat(auto-fit,minmax(min(230px,100%),1fr))!important}.espacio-card,.overview-card,.overview-kpi{min-width:0;max-width:100%}.featured-upgrade-benefits{grid-template-columns:1fr}.featured-upgrade-modal{padding:1.2rem}.featured-upgrade-actions{display:grid;grid-template-columns:1fr 1fr}}
      @media(max-width:480px){.overview-kpis{gap:.65rem}.overview-kpi{grid-template-columns:34px minmax(0,1fr);min-height:100px;padding:.7rem}.overview-kpi .kpi-icon{width:34px;height:34px}.quick-actions{grid-template-columns:repeat(2,minmax(0,1fr));gap:.5rem}.quick-actions button{min-height:62px;padding:.6rem}.welcome-panel{padding:1rem}.welcome-panel h2{font-size:1.15rem}.page-heading p{max-width:32ch}}
      @media(max-width:360px){.overview-kpis,.quick-actions{grid-template-columns:1fr}}
      @media(max-width:600px){.space-upgrade-options{grid-template-columns:1fr}.custom-space-quote{padding:.75rem}}
      @media(min-width:601px) and (max-width:1024px){.espacios-grid{grid-template-columns:repeat(auto-fill,minmax(min(280px,100%),1fr))!important;justify-content:stretch}.espacio-card{width:100%;max-width:none}}
      @media(max-width:600px){.espacios-grid{grid-template-columns:minmax(0,1fr)!important}.espacio-card{width:100%;max-width:none;justify-self:stretch}.card-details{grid-template-columns:repeat(2,minmax(0,1fr))}}
      /* Encabezado de «Mis espacios»: botones en una línea y de igual alto. */
      .page-heading:has(.spaces-heading-actions){flex-wrap:wrap;row-gap:.9rem}
      .spaces-heading-actions{display:flex;flex-wrap:wrap;align-items:center;justify-content:flex-end;gap:.6rem;margin-left:auto}
      @media(max-width:760px){.spaces-heading-actions{flex-wrap:wrap;margin-left:0}}
      .spaces-heading-actions>*{height:48px!important;min-height:48px!important;align-self:center;box-sizing:border-box}
      .spaces-heading-actions>.btn-secondary,.spaces-heading-actions>.btn-primary{display:inline-flex;align-items:center;justify-content:center;gap:.5rem;padding:0 1rem!important;white-space:nowrap}
      .spaces-heading-actions>.btn-secondary i,.spaces-heading-actions>.btn-primary i{font-size:1rem}
      /* Foto que no carga: el mismo respaldo que una tarjeta sin imagen. */

      /* --- Panel de inicio (rediseño): banner, resumen y dos columnas ---
         Columna principal: agenda + galería. Columna lateral: próxima
         reserva + tareas + consejo. Alturas independientes para que ninguna
         columna deje un hueco vacío bajo la otra. */
      .control-overview{display:grid;gap:1rem;min-width:0}
      .overview-main{display:grid;grid-template-columns:minmax(0,1.62fr) minmax(268px,.78fr);grid-template-rows:auto auto;gap:1rem;align-items:stretch;min-width:0}
      .overview-main.no-agenda{grid-template-columns:minmax(0,1fr)}
      .overview-primary,.overview-side{display:contents}
      .overview-primary>app-dashboard-agenda{grid-column:1;grid-row:1}
      .overview-primary>app-dashboard-spaces{grid-column:1;grid-row:2}
      .overview-side>app-dashboard-next-reservation{grid-column:2;grid-row:1}
      .overview-side>app-dashboard-tasks{grid-column:2;grid-row:2}
      .overview-primary>app-dashboard-spaces,.overview-side>app-dashboard-tasks{height:auto;align-self:start}
      .overview-main.no-agenda .overview-primary>app-dashboard-spaces{grid-row:1}
      @media(max-width:1180px){
        .overview-main{grid-template-columns:minmax(0,1fr);grid-template-rows:auto}
        .overview-primary,.overview-side{display:grid;gap:1rem;min-width:0;align-content:start}
        .overview-primary>app-dashboard-agenda,.overview-primary>app-dashboard-spaces,.overview-side>app-dashboard-next-reservation,.overview-side>app-dashboard-tasks{grid-column:auto;grid-row:auto}
      }

  `]
})
export class DashboardComponent implements OnInit {
  readonly formatSpacesLabel = formatSpacesLabel;
    private reservasComponent?: ReservasComponent;
    private pendingReservationSpaceId: number | null | undefined;

    @ViewChild('reservasRef')
    set reservasRef(component: ReservasComponent | undefined) {
      this.reservasComponent = component;
      if (component && this.pendingReservationSpaceId !== undefined) {
        const spaceId = this.pendingReservationSpaceId;
        this.pendingReservationSpaceId = undefined;
        component.openCreateForm(spaceId);
      }
    }

    get reservasRef(): ReservasComponent | undefined {
      return this.reservasComponent;
    }
    private readonly destroyRef = inject(DestroyRef);

    openNewReservation(space?: Espacio): void {
      if (!this.can('reservations')) return;
      const spaceId = space?.space_id ?? null;
      if (this.activeTab() === 'reservas' && this.reservasRef) {
        this.reservasRef.openCreateForm(spaceId);
        return;
      }

      // En el resumen, <app-reservas> no está montado por el *ngIf. Se
      // conserva la intención hasta que Angular cree el componente al
      // cambiar de módulo; el setter de ViewChild abre entonces el modal.
      this.pendingReservationSpaceId = spaceId;
      this.selectTab('reservas');
    }

    onReservationsPageSizeChange(event: Event): void {
      const value = (event.target as HTMLSelectElement).value;
      this.reservasRef?.onPageSizeChange(value);
    }
    activeTab = signal<DashboardTab>('overview');
    superAdminOpen = signal(false);
    superAdminSection = signal('overview');
    superAdminSections = [
      { id: 'overview', label: 'Resumen general', icon: 'bi bi-grid-1x2' },
      { id: 'estadisticas', label: 'Estadísticas globales', icon: 'bi bi-bar-chart-line' },
      { id: 'owners', label: 'Propietarios', icon: 'bi bi-person-badge' },
      { id: 'users', label: 'Usuarios', icon: 'bi bi-people' },
      { id: 'personal', label: 'Personal administrativo de TuSala', icon: 'bi bi-shield-person' },
      { id: 'subs', label: 'Suscripciones', icon: 'bi bi-gem' },
      { id: 'planes', label: 'Planes', icon: 'bi bi-layers' },
      { id: 'pagos', label: 'Pagos de suscripciones', icon: 'bi bi-credit-card' },
      { id: 'pagosNegocios', label: 'Pagos a negocios', icon: 'bi bi-building-check' },
      { id: 'personalizacion', label: 'Personalización', icon: 'bi bi-palette' },
    ];
    // Corrección pedida: antes arrancaba en `false` a secas -- mientras la
    // suscripción real todavía no llegaba (getSubscription() es async),
    // [locked]="!hasAdvancedStatistics()" ya daba `true`, así que la
    // pantalla de Estadísticas mostraba el aviso de "acceso restringido"
    // de entrada, aunque el plan real SÍ incluyera el beneficio -- y un
    // instante después, al resolver la suscripción, cambiaba de golpe a la
    // vista completa. Ese parpadeo (bloqueado -> desbloqueado) es
    // exactamente lo reportado. `null` distingue "todavía no se sabe" de
    // "ya se sabe que no lo incluye" -- ver accessPending en
    // estadisticas.component.ts, que usa esa distinción para no mostrar
    // el aviso de plan restringido hasta estar seguros.
    hasAdvancedStatistics = signal<boolean | null>(null);
    subscriptionPlan = signal<string>('');
    currentSubscription = signal<any | null>(null);
    publicPlans = signal<any[]>([]);
    showSpaceLimitModal = signal(false);
    /** Modal "Días no disponibles" (cabecera de Espacios). */
    readonly showAvailabilityBlocks = signal(false);
    showLogoutConfirm = signal(false);
    showFeaturedUpgrade = signal(false);
    spacePendingDeletion = signal<any | null>(null);
    deletingSpace = signal(false);
    togglingPublicationId = signal<number | null>(null);
    businesses = signal<SuperAdminBusiness[]>([]);
    featuredSpaceSavingId = signal<number | null>(null);
    privateSpaceSavingId = signal<number | null>(null);
    /** null = todavía no se consultó (ver loadDependenciesFor). Si el
     * negocio ENTERO es privado, privatizar un espacio puntual es
     * redundante -- el botón de cada tarjeta se oculta en ese caso (ver
     * plantilla) en vez de dejar una acción que no tiene ningún efecto
     * nuevo sobre lo que ya cubre la privacidad del negocio. */
    // Bug real corregido: era un signal propio cargado una sola vez, así que
    // activar/desactivar la privacidad en "Mi sitio" dejaba esta pestaña con
    // el estado viejo (botones y avisos equivocados) hasta recargar. Ahora es
    // el mismo estado compartido que actualizan "Mi sitio" y la barra superior.
    businessPrivacyInfo = computed<BusinessPrivacyInfo | null>(() => this.businessConfigService.privacyState());
    loadBusinessPrivacyInfo(): void {
      const businessId = this.currentUser()?.id;
      if (!businessId) return;
      this.businessConfigService.obtenerPrivacidad(businessId).subscribe({ error: () => {} });
    }

    /** Etiqueta de visibilidad de cada tarjeta. Sin el beneficio de plan no
     * se muestra: en ese caso no existe privacidad que indicar. */
    spaceVisibility(espacio: Espacio): { private: boolean; label: string; hint: string } | null {
      const info = this.businessPrivacyInfo();
      if (!info?.featureEnabled) return null;
      if (info.isPrivate) return { private: true, label: 'Privado · negocio', hint: 'Todo tu negocio es privado: este espacio solo se ve con el código de acceso.' };
      if (espacio.is_private) return { private: true, label: 'Privado', hint: 'Solo se ve con el código de acceso de tu negocio.' };
      return { private: false, label: 'Público', hint: 'Visible para cualquiera en la galería y en tu sitio.' };
    }
    private overviewReservationsLoaded = false;
    spaceSearch = signal('');
    businessesLoading = signal<boolean>(false);
    selectedBusiness = signal<SuperAdminBusiness | null>(null);

    // --- "Mi sitio": enlace público personalizado del negocio (edición y
    // vista previa completas viven en Configuración; aquí solo el botón de
    // acceso directo "Compartir mi sitio web"). -----------------------------
    canUseOwnSite = computed(() =>
      !!this.currentUser()?.subscription_exempt || ['estandar', 'premium', 'gratis'].includes(this.subscriptionPlan())
    );
    mySiteSharing = signal(false);
    @ViewChild(OwnerOnboardingComponent) onboardingRef?: OwnerOnboardingComponent;
    private readonly IMAGES_BASE_URL = 'https://storage.googleapis.com/thehubp/spaces/';

    /** getters reactivos de novedades (computados desde el servicio) */
    get businessUnread() { return this.noveltyService.businessUnread; }
    get globalUnread() { return this.noveltyService.globalUnread; }

    constructor(
      private authService: AuthService,
      private router: Router,
      private route: ActivatedRoute,
      public salaService: SalaService,
      private subscriptionService: SubscriptionService,
      private employeeService: EmployeeService,
      private dialogs: DialogService,
      private notifications: NotificationService,
      private noveltyService: NoveltyService,
      public reservationService: ReservationService,
      private editService: ReservationEditService,
      public contacts: PlatformContactsService,
      private miSitioUnsavedChanges: MiSitioUnsavedChangesService,
      private businessConfigService: BusinessConfigService,
      readonly cooldown: ActionCooldownService,
      public dash: DashboardService,
    ) {
      if (this.isSuperAdmin()) {
        this.loadBusinesses();
      }
      this.contacts.load();
    }

    ngOnInit(): void {
      const requestedModule = this.route.snapshot.queryParamMap.get('module');
      const requestedTab = requestedModule as DashboardTab | null;
      const requestedSection = this.route.snapshot.queryParamMap.get('section');
      if (requestedModule === 'auditoria') {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { module: null, section: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
      if (requestedTab && this.isTabAllowed(requestedTab)) {
        this.activeTab.set(requestedTab);
        if (requestedTab === 'superadmin' && requestedSection && this.superAdminSections.some(section => section.id === requestedSection)) {
          this.superAdminSection.set(requestedSection);
          this.superAdminOpen.set(true);
        }
      } else if (this.currentUser()?.role === 'employee') {
        const first = ([['spaces','salas'],['reservations','reservas'],['customers','usuarios'],['recycle','reciclaje'],['statistics','estadisticas'],['settings','configuracion'],['employees','subadministrador']] as const).find(([module]) => this.can(module));
        if (first) this.activeTab.set(first[1]);
      }
      const storedTab = (() => { try { return localStorage.getItem('dashboard_tab') as DashboardTab | null; } catch { return null; } })();
      const storedSection = (() => { try { return localStorage.getItem('dashboard_section'); } catch { return null; } })();
      if ((storedTab as string | null) === 'auditoria') {
        try { localStorage.removeItem('dashboard_tab'); } catch {}
      }
      if (storedTab && this.isTabAllowed(storedTab) && this.activeTab() === 'overview') {
        if (storedTab === 'superadmin' && storedSection && this.superAdminSections.some(section => section.id === storedSection)) {
          this.activeTab.set('superadmin');
          this.superAdminSection.set(storedSection);
          this.superAdminOpen.set(true);
        } else if (storedTab !== 'superadmin') {
          this.activeTab.set(storedTab);
        }
      }
      this.route.queryParamMap
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(params => {
          const requestedModule = params.get('module');
          const module = requestedModule as DashboardTab | null;
          const section = params.get('section');
          if (requestedModule === 'auditoria') {
            void this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { module: null, section: null },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
            return;
          }
          // /crear-sala (enlaces antiguos o directos) llega aquí con crear=1.
          if (params.get('crear') === '1') {
            void this.router.navigate([], { relativeTo: this.route, queryParams: { crear: null }, queryParamsHandling: 'merge', replaceUrl: true });
            setTimeout(() => this.createEspacio());
          }
          if (!module || !this.isTabAllowed(module)) return;
          this.activeTab.set(module);
          if (module === 'superadmin' && section && this.superAdminSections.some(item => item.id === section)) {
            this.superAdminSection.set(section);
            this.superAdminOpen.set(true);
          }
          this.saveTabState(module, module === 'superadmin' ? this.superAdminSection() : undefined);
          this.resetWorkspaceScroll();
          this.loadDependenciesFor(module);
        });
      this.resetWorkspaceScroll();
      // Carga inmediata de los contadores de novedades. start*Polling ya pide
      // el contador (antes además se llamaba a refresh*Count justo antes: la
      // misma consulta salía dos veces al entrar).
      const bizId = this.currentUser()?.id || 0;
      if (bizId) this.noveltyService.startBusinessPolling(bizId);
      if (this.isSuperAdmin()) this.noveltyService.startGlobalPolling();

      this.loadDependenciesFor(this.activeTab());
      if (this.currentUser()?.subscription_exempt) {
        this.subscriptionPlan.set('Acceso completo');
        this.hasAdvancedStatistics.set(true);
      } else this.subscriptionService.getSubscription().subscribe({
          next: ({ subscription }) => {
            this.currentSubscription.set(subscription as any);
            this.subscriptionPlan.set((subscription as any)?.plan || '');
            const benefit = (subscription as any)?.planDefinition?.benefits?.advanced_statistics;
            this.hasAdvancedStatistics.set(benefit === true || benefit === 1 || benefit === '1' || benefit === 'true');
          },
          error: () => { this.currentSubscription.set(null); this.subscriptionPlan.set(''); this.hasAdvancedStatistics.set(false); },
      });
      if (!this.currentUser()?.subscription_exempt) {
        this.subscriptionService.getPublicPlans().subscribe({ next: response => this.publicPlans.set(response?.plans || []), error: () => this.publicPlans.set([]) });
      }
      // Los espacios ya se cargan automáticamente en el servicio
    }

    get currentUser() {
      return this.authService.currentUser;
    }

    /** Dentro del modal "Gestionar negocio" de Super Admin. */
    isBusinessContextMode(): boolean {
      return this.authService.isBusinessContextMode();
    }

    isSuperAdmin(): boolean {
      return (this.authService.currentUser() as any)?.role === 'super_admin';
    }

    isPrimaryPlatformAdmin(): boolean {
      const user: any = this.authService.currentUser();
      const email = String(user?.session_user?.email || user?.email || '').trim().toLowerCase();
      return user?.role === 'super_admin' && email === 'admin@tusala.net';
    }

    can(module: string, action = 'view'): boolean { return this.authService.hasPermission(module, action); }

    moduleSkeletonVariant(): 'cards'|'list'|'table'|'form'|'detail' {
      const module = this.activeTab();
      if (module === 'configuracion') return 'form';
      if (module === 'calendario') return 'list';
      if (['reservas','usuarios','reciclaje','subadministrador','novedades','superadmin','superadminNovedades'].includes(module)) return 'table';
      return 'cards';
    }

    private saveTabState(tab: DashboardTab, section?: string): void {
      try {
        localStorage.setItem('dashboard_tab', tab);
        if (section) localStorage.setItem('dashboard_section', section);
      } catch {}
    }

    // fragment opcional: Angular Router, por defecto (sin fragment ni
    // preserveFragment), DESCARTA el hash actual de la URL en cada
    // navegación — normalmente eso es lo deseado (limpia un hash viejo al
    // cambiar de módulo desde el sidebar), pero cuando SÍ hay un hash de
    // destino específico (p. ej. el botón "Ir" de Kubo, que ya dejó el
    // hash correcto en window.location.hash antes de llamar acá) hay que
    // pasarlo explícitamente para que el propio router no lo borre un
    // instante después de haberse fijado.
    selectTab(tab: DashboardTab, fragment?: string, extraQueryParams?: Record<string, string | number | null>): void {
      if (!this.isTabAllowed(tab)) return;
      // "Mi sitio" ya no tiene botones de guardar por campo -- si el dueño
      // se va con cambios sin guardar los perdería en silencio, así que se
      // confirma antes de cambiar de pestaña (ver MiSitioUnsavedChangesService).
      if (this.activeTab() === 'miSitio' && tab !== 'miSitio' && this.miSitioUnsavedChanges.hasUnsavedChanges()) {
        this.miSitioUnsavedChanges.confirmLeave().then(canLeave => {
          if (canLeave) this.performTabSwitch(tab, fragment, extraQueryParams);
        });
        return;
      }
      this.performTabSwitch(tab, fragment, extraQueryParams);
    }

    private performTabSwitch(tab: DashboardTab, fragment?: string, extraQueryParams?: Record<string, string | number | null>): void {
      this.activeTab.set(tab);
      this.resetWorkspaceScroll();
      this.saveTabState(tab, tab === 'superadmin' ? this.superAdminSection() : undefined);
      this.loadDependenciesFor(tab);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: tab === 'overview'
          ? { module: null, section: null, tab: null }
          : { module: tab, section: tab === 'superadmin' ? this.superAdminSection() : null, tab: null, ...extraQueryParams },
        queryParamsHandling: 'merge',
        replaceUrl: true,
        ...(fragment ? { fragment } : {}),
      }).then(() => {
        // Reafirma el tab activo (y por lo tanto el resaltado del botón
        // del sidebar, ligado a activeTab()) una vez que el router terminó
        // de aplicar la navegación — cubre cualquier caso donde la
        // suscripción reactiva a queryParamMap (más abajo) llegue a pisar
        // este valor con algo desactualizado durante la transición.
        if (this.activeTab() !== tab) this.activeTab.set(tab);
      });
      window.setTimeout(() => this.onboardingRef?.refresh(), 250);
    }

    navigateFromOnboarding(module: OnboardingModule): void {
      // owner-onboarding.component.ts ya dejó el hash de destino (si
      // corresponde) en window.location.hash ANTES de emitir este evento —
      // se reenvía tal cual para que selectTab() lo preserve.
      const hash = window.location.hash.replace(/^#/, '');
      this.selectTab(module, hash || undefined);
    }

    private resetWorkspaceScroll(): void {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }));
    }

    /**
     * Envío controlado del enlace de restablecimiento de contraseña.
     * Usa AuthService.requestPasswordRecovery(email) y maneja estados UI
     * (bloqueo de botón, notificaciones). Si no se proporciona email
     * intenta usar el correo asociado al usuario conectado.
     */
    requestPasswordRecovery(email?: string): void {
      const candidate = (email && String(email).trim()) || (this.currentUser()?.session_user?.email) || (this.currentUser() as any)?.email;
      if (!candidate) {
        this.notifications.error('No hay correo disponible para enviar el enlace de restablecimiento.');
        return;
      }
      if (this.resendingResetLink()) return;
      this.resendingResetLink.set(true);
      this.authService.requestPasswordRecovery(candidate).subscribe({
        next: (resp: any) => {
          this.resendingResetLink.set(false);
          this.notifications.success(resp?.msg || 'Se envió el enlace de restablecimiento al correo indicado.');
        },
        error: (err: any) => {
          this.resendingResetLink.set(false);
          this.notifications.error(friendlyError(err, 'No fue posible enviar el enlace de restablecimiento.'));
        }
      });
    }

    hasPaginatedModule(): boolean {
      return ['reservas', 'usuarios', 'reciclaje', 'subadministrador', 'novedades', 'superadmin', 'superadminNovedades'].includes(this.activeTab());
    }

    private loadDependenciesFor(tab: DashboardTab): void {
      // El panel de inicio reúne sus propios datos (espacios, reservas del mes
      // y contadores) en una sola carga con cache por mes.
      if (tab === 'overview') {
        this.dash.load();
      }
      if (tab === 'salas' && this.can('spaces') && !this.salaService.espaciosInitialized() && !this.salaService.espaciosLoading()) {
        this.reloadEspacios();
      }
      if (tab === 'salas' && this.can('spaces') && this.businessPrivacyInfo() === null) {
        this.loadBusinessPrivacyInfo();
      }
    }

    onEditReservationFromCalendar(reservation: Reservation): void {
      this.editService.requestEdit(reservation);
      this.selectTab('reservas');
    }

    // ---- Panel de inicio (rediseño) ----------------------------------------

    /** Ancho compacto: la agenda cambia a lista cronológica. */
    compactLayout = signal(typeof window !== 'undefined' && window.innerWidth <= 900);

    @HostListener('window:resize')
    onWindowResize(): void {
      this.compactLayout.set(window.innerWidth <= 900);
    }

    /** Imagen real de la próxima reserva; no sustituye el espacio con fotos genéricas. */
    nextReservationImage(): string | null {
      const reservation = this.dash.nextReservation();
      const space = reservation
        ? this.dash.spaces().find(item => item.space_id === reservation.space_id)
        : null;
      return space?.images?.find(image => !!image.image_url)?.image_url || null;
    }

    onStatSelect(key: StatKey): void {
      const target: Record<StatKey, DashboardTab> = {
        reservations: 'reservas',
        spaces: 'salas',
        customers: 'usuarios',
        payments: 'reservas',
      };
      this.openModule(target[key]);
    }

    /** Navega a un módulo de la barra lateral comprobando el permiso real. */
    openModule(module: string): void {
      if (this.isTabAllowed(module as DashboardTab)) this.selectTab(module as DashboardTab);
    }

    shiftDay(delta: number): void {
      const date = new Date(`${this.dash.selectedDate()}T00:00:00`);
      date.setDate(date.getDate() + delta);
      this.dash.selectDate(this.localIso(date));
    }

    goToToday(): void {
      this.dash.selectDate(this.localIso(new Date()));
    }

    /** Contacto con el cliente por el medio que ya exista (correo o teléfono). */
    contactCustomer(reservation: Reservation): void {
      const email = reservation.customer?.email || reservation.guest_email || '';
      const phone = reservation.customer?.phone_number || reservation.guest_phone || '';
      if (email) {
        window.location.href = `mailto:${email}?subject=${encodeURIComponent(`Tu reserva en ${this.businessName()}`)}`;
        return;
      }
      if (phone) window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
    }

    onSpaceMenu(event: { action: SpaceMenuAction; space: Espacio }): void {
      if (event.action === 'reserve') {
        this.openNewReservation(event.space);
        return;
      }
      this.viewDetalle(event.space.space_id);
    }

    private localIso(date: Date): string {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day}`;
    }

    private isTabAllowed(tab: DashboardTab): boolean {
      if (tab === 'overview' || tab === 'novedades') return true;
      if (tab === 'superadmin' || tab === 'superadminNovedades') return this.isSuperAdmin();
      if (tab === 'suscripcion') return this.currentUser()?.role !== 'employee' && !this.currentUser()?.subscription_exempt;
      if (tab === 'ingresosNegocio') return this.currentUser()?.role === 'owner' || this.isSuperAdmin();
      const permissions: Partial<Record<DashboardTab, string>> = {
        salas: 'spaces', reservas: 'reservations', calendario: 'reservations',
        // Oculto temporalmente a pedido del usuario -- ver el comentario
        // junto a la ruta '/reembolsos' en app.routes.ts.
        // reembolsos: 'reservations',
        usuarios: 'customers', reciclaje: 'recycle', estadisticas: 'statistics',
        configuracion: 'settings', miSitio: 'settings', subadministrador: 'employees',
      };
      return permissions[tab] ? this.can(permissions[tab]!) : false;
    }

    showSuperAdmin(): void {
      this.superAdminOpen.set(!this.superAdminOpen());
      this.superAdminSection.set('overview');
      this.saveTabState('superadmin', 'overview');
      this.selectTab('superadmin');
    }

    openSuperAdmin(section: string): void {
      this.superAdminSection.set(section);
      this.saveTabState('superadmin', section);
      this.selectTab('superadmin');
    }

    businessName(): string { const user: any = this.currentUser(); return user?.name || user?.business_name || user?.company || 'Mi negocio'; }
    connectedUserName(): string {
      const user: any = this.currentUser();
      return String(user?.session_user?.name || user?.owner?.name || 'Usuario');
    }
    firstName(): string { return this.connectedUserName().trim().split(/\s+/)[0]; }
    userInitials(): string {
      const name = this.connectedUserName().trim();
      return name.split(/\s+/).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || 'TS';
    }
    roleLabel(): string {
      const role = (this.currentUser() as any)?.role;
      return role === 'super_admin' ? 'Super Administrador' : role === 'employee' ? 'Sub-Administrador' : 'Propietario';
    }
    // Antes solo ponía en mayúscula la primera letra del código interno del
    // plan ("estandar" -> "Estandar", sin tilde) -- se veía como el propio
    // valor interno reformateado, no como el nombre real del plan.
    formatPlanLabel(plan: string | null | undefined): string {
      const value = String(plan || '').trim();
      if (!value) return 'Activo';
      const labels: Record<string, string> = { gratis: 'Plan Gratis', basico: 'Plan Básico', estandar: 'Plan Estándar', premium: 'Plan Premium' };
      return labels[value] || (value.charAt(0).toLocaleUpperCase('es-SV') + value.slice(1));
    }
    formatSubscriptionStatusLabel(status: string | null | undefined): string {
      const value = String(status || '').trim();
      if (!value) return '—';
      const labels: Record<string, string> = { active: 'Activa', pending: 'Pendiente', inactive: 'Inactiva', cancelled: 'Cancelada', expired: 'Expirada', trial: 'Prueba gratuita' };
      return labels[value] || value;
    }
    formatReservationStatusLabel(status: string | null | undefined): string {
      const value = String(status || '').trim();
      if (!value) return '—';
      const labels: Record<string, string> = { pending: 'Pendiente', confirmed: 'Confirmada', cancelled: 'Cancelada', completed: 'Completada' };
      return labels[value] || value;
    }
    isTrialPlan(): boolean {
      return this.currentUser()?.subscription?.status === 'trial';
    }
    todayLabel(): string {
      return new Intl.DateTimeFormat('es-SV', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date());
    }
    openLogoutConfirm(): void { this.showLogoutConfirm.set(true); }
    closeLogoutConfirm(): void { this.showLogoutConfirm.set(false); }

    moduleTitle(): string {
      const titles: Record<string, string> = { overview: 'Dashboard', salas: 'Mis espacios', reservas: 'Reservas', calendario: 'Calendario', usuarios: 'Clientes', reciclaje: 'Papelera', estadisticas: 'Estadísticas', configuracion: 'Configuración', miSitio: 'Mi sitio', ingresosNegocio: 'Ingresos y transferencias', subadministrador: 'Sub-Administrador', novedades: 'Novedades', superadminNovedades: 'Novedades globales', suscripcion: 'Mi suscripción' };
      return this.activeTab() === 'superadmin' ? (this.superAdminSections.find(s => s.id === this.superAdminSection())?.label || 'Resumen Super Admin') : (titles[this.activeTab()] || '');
    }
    moduleDescription(): string {
      const descriptions: Record<string, string> = {
        overview: 'Resumen de tu operación en TuSala.',
        salas: 'Administra el catálogo, disponibilidad y presentación de tus espacios.',
        reservas: 'Consulta y gestiona las reservas del negocio.',
        calendario: 'Visualiza ocupación, horarios y disponibilidad.',
        usuarios: 'Administra clientes y accesos relacionados con tu negocio.',
        estadisticas: 'Analiza el desempeño de espacios y reservas.',
        reciclaje: 'Restaura registros eliminados dentro del período disponible.',
        configuracion: 'Gestiona identidad, seguridad y políticas del negocio.',
        miSitio: 'Personaliza la información y apariencia de tu página pública.',
        ingresosNegocio: 'Consulta tus cobros y administra de forma segura dónde recibirás tus transferencias.',
        subadministrador: 'Controla colaboradores y permisos administrativos.',
        novedades: 'Revisa avisos y actividad relevante del negocio.',
        superadminNovedades: 'Comunicación global de la plataforma.',
        suscripcion: 'Plan, beneficios, renovación e historial de pagos.',
        superadmin: 'Administración global y métricas de la plataforma.'
      };
      if (this.activeTab() === 'superadmin' && this.superAdminSection() === 'personalizacion') {
        return 'Identidad y elementos globales que TuSala muestra en todos sus frentes.';
      }
      return descriptions[this.activeTab()] || 'Centro de operaciones TuSala.';
    }

    reloadEspacios(): void {
      this.salaService.loadEspacios(1, this.spaceSearch());
    }

    searchSpaces(value: string): void {
      if (!this.cooldown.consume('spaces-search')) return;
      this.spaceSearch.set(value.trim());
      // Búsqueda explícita del usuario (botón "Buscar" o Enter): se aplica
      // el tiempo mínimo de carga visible, a diferencia de una recarga
      // automática (reloadEspacios sin búsqueda).
      this.salaService.loadEspacios(1, this.spaceSearch(), true);
    }

    clearSpaceSearch(input: HTMLInputElement): void {
      input.value = '';
      this.spaceSearch.set('');
      this.reloadEspacios();
    }

    handleFeaturedSpace(space: Espacio): void {
      if (this.salaService.featuredListingEnabled()) {
        this.toggleFeaturedSpace(space);
        return;
      }
      this.openFeaturedBenefit();
    }

    openFeaturedBenefit(): void {
      if (this.salaService.featuredListingEnabled()) return;
      this.showFeaturedUpgrade.set(true);
    }

    closeFeaturedBenefit(): void {
      this.showFeaturedUpgrade.set(false);
    }

    goToPremiumPlans(): void {
      this.closeFeaturedBenefit();
      this.selectTab('suscripcion');
    }

    toggleFeaturedSpace(space: Espacio): void {
      if (!this.cooldown.consume('toggle-featured')) return;
      if (this.featuredSpaceSavingId() !== null) return;
      this.featuredSpaceSavingId.set(space.space_id);
      this.salaService.setFeaturedListing(space.space_id, !space.is_featured_listing).subscribe({
        next: response => {
          this.salaService.espacios.update(items => items.map(item =>
            item.space_id === space.space_id ? { ...item, ...response.space } : item
          ));
          this.salaService.featuredListingSelectedCount.set(response.featuredListing.selectedCount);
          this.notifications.success(response.msg);
          this.featuredSpaceSavingId.set(null);
        },
        error: error => {
          this.notifications.error(friendlyError(error, 'No fue posible actualizar el espacio destacado.'));
          this.featuredSpaceSavingId.set(null);
        },
      });
    }

    togglePrivateSpace(space: Espacio): void {
      if (!this.cooldown.consume('toggle-private-space')) return;
      if (this.privateSpaceSavingId() !== null) return;
      this.privateSpaceSavingId.set(space.space_id);
      this.salaService.setSpacePrivacy(space.space_id, !space.is_private).subscribe({
        next: response => {
          this.salaService.espacios.update(items => items.map(item =>
            item.space_id === space.space_id ? { ...item, ...response.space } : item
          ));
          this.notifications.success(response.msg);
          this.privateSpaceSavingId.set(null);
          // Puede haber generado el código del negocio por primera vez (ver
          // setSpacePrivacyStatus) -- se refresca para que "Copiar enlace"
          // ya tenga el código correcto sin esperar a re-entrar a la pestaña.
          if (!space.is_private) this.loadBusinessPrivacyInfo();
        },
        error: error => {
          this.notifications.error(friendlyError(error, 'No fue posible actualizar la privacidad del espacio.'));
          this.privateSpaceSavingId.set(null);
        },
      });
    }

    /** El editor de imagen para imprimir vive en "Mi sitio" (misma plantilla
     * para todo el negocio o un espacio puntual, ver InvitationEditorComponent) --
     * este botón solo lleva hasta ahí, con este espacio ya preseleccionado, en
     * vez de duplicar el editor completo dentro de "Gestión de salas". */
    goToCreatePrintImage(espacio: Espacio): void {
      this.selectTab('miSitio', undefined, { section: 'privado', space: espacio.space_id });
    }

    loadBusinesses(): void {
      this.businessesLoading.set(true);
      this.subscriptionService.getAllBusinesses(1, 100).subscribe({
        next: (response) => {
          this.businesses.set(response.businesses || []);
          this.businessesLoading.set(false);
        },
        error: (error) => {
          console.error('Error al cargar negocios:', error);
          this.businesses.set([]);
          this.businessesLoading.set(false);
        }
      });
    }

    openBusinessModal(business: SuperAdminBusiness): void {
      this.selectedBusiness.set(business);
    }

    closeBusinessModal(): void {
      this.selectedBusiness.set(null);
    }

    getImageUrl(imageUrl: string): string {
      return `${this.IMAGES_BASE_URL}${imageUrl}`;
    }

    getCoverImage(space: Espacio): EspacioImage {
      return space.images.find(image => image.is_featured) || space.images[0];
    }

    categoryLabels(space: Espacio): string {
      const labels: Record<string, string> = { Coworking: 'Espacio de coworking', 'Sala de capacitación': 'Sala para capacitaciones', 'Sala de entrevistas': 'Sala para entrevistas o consultas', 'Sala de juntas': 'Sala de juntas directivas', Eventos: 'Salón para eventos y celebraciones', Conferencias: 'Auditorio o sala de conferencias', 'Estudio creativo': 'Taller o estudio creativo', Podcast: 'Estudio para podcast y grabación de audio', Fotografía: 'Estudio fotográfico', Video: 'Estudio de video y producción audiovisual', 'Taller práctico': 'Espacio para talleres prácticos', 'Estudio de música y ensayo': 'Estudio de música y sala de ensayo', 'Bienestar y yoga': 'Espacio para bienestar, yoga o terapias', 'Sala de exhibición': 'Sala de exhibición o showroom', 'Videojuegos y esports': 'Sala de videojuegos y esports', 'Cabina para videollamadas': 'Cabina privada para videollamadas', 'Espacio infantil': 'Espacio para actividades infantiles', Otro: 'Otro tipo de espacio' };
      return (space.categories?.length ? space.categories : [space.category || 'Sin categoría']).map(value => labels[value] || value).join(' · ');
    }

    visibleCategoryLabels(space: Espacio): string {
      return this.spaceCategoryLabels(space).slice(0, 2).join(' · ');
    }

    remainingCategoryCount(space: Espacio): number {
      return Math.max(0, this.spaceCategoryLabels(space).length - 2);
    }

    private spaceCategoryLabels(space: Espacio): string[] {
      const labels: Record<string, string> = { Coworking: 'Espacio de coworking', 'Sala de capacitación': 'Sala para capacitaciones', 'Sala de entrevistas': 'Sala para entrevistas o consultas', 'Sala de juntas': 'Sala de juntas directivas', Eventos: 'Salón para eventos y celebraciones', Conferencias: 'Auditorio o sala de conferencias', 'Estudio creativo': 'Taller o estudio creativo', Podcast: 'Estudio para podcast y grabación de audio', Fotografía: 'Estudio fotográfico', Video: 'Estudio de video y producción audiovisual', 'Taller práctico': 'Espacio para talleres prácticos', 'Estudio de música y ensayo': 'Estudio de música y sala de ensayo', 'Bienestar y yoga': 'Espacio para bienestar, yoga o terapias', 'Sala de exhibición': 'Sala de exhibición o showroom', 'Videojuegos y esports': 'Sala de videojuegos y esports', 'Cabina para videollamadas': 'Cabina privada para videollamadas', 'Espacio infantil': 'Espacio para actividades infantiles', Otro: 'Otro tipo de espacio' };
      return (space.categories?.length ? space.categories : [space.category || 'Sin categoría']).map(value => labels[value] || value);
    }

    requestSpacePublicationChange(space: Espacio): void {
      if (!space.is_active) {
        this.toggleSpacePublication(space);
        return;
      }
      this.dialogs.confirmAndRun(
        {
          title: `¿Despublicar "${space.title}"?`,
          message: 'El espacio dejará de estar disponible para nuevas búsquedas y reservas de clientes.\n\n· Perderá visibilidad: ya no aparecerá en el catálogo público.\n· No recibirá nuevas reservas mientras esté oculto.\n· Revisa sus reservas actuales: despublicar no elimina automáticamente compromisos ya registrados.\n\nPodrás publicarlo nuevamente desde esta misma tarjeta.',
          tone: 'warning',
          confirmText: 'Despublicar espacio',
          cancelText: 'Mantener publicado',
        },
        () => this.salaService.toggleEspacioStatus(space.space_id, false),
        'Espacio despublicado.',
        error => friendlyError(error, 'No fue posible cambiar la publicación.'),
      ).then(updated => {
        if (updated) this.salaService.espacios.update(items => items.map(item => item.space_id === space.space_id ? { ...item, ...updated } : item));
      }).catch(() => {});
    }

    toggleSpacePublication(space: Espacio): void {
      if (!this.cooldown.consume('toggle-publication')) return;
      if (this.togglingPublicationId() !== null) return;
      this.togglingPublicationId.set(space.space_id);
      this.salaService.toggleEspacioStatus(space.space_id, !space.is_active).subscribe({
        next: updated => {
          this.salaService.espacios.update(items => items.map(item => item.space_id === space.space_id ? { ...item, ...updated } : item));
          this.notifications.success(updated.is_active ? 'Espacio publicado.' : 'Espacio despublicado.');
        },
        error: error => {
          this.notifications.error(friendlyError(error, 'No fue posible cambiar la publicación.'));
        },
        complete: () => {
          this.togglingPublicationId.set(null);
        },
      });
    }

    /** Una foto inaccesible usa el mismo estado neutro que un espacio sin foto. */
    failedSpaceImages = new Set<number>();
    handleImageError(spaceId: number): void { this.failedSpaceImages.add(spaceId); }

    viewDetalle(spaceId: number): void {
      this.router.navigate(['/sala', spaceId]);
    }

    editEspacio(spaceId: number): void {
      console.log('Editar espacio:', spaceId);
      this.router.navigate(['/sala', spaceId]);
    }

    deleteEspacio(spaceId: number): void { if (!this.cooldown.consume('delete-space')) return; this.spacePendingDeletion.set(this.salaService.espacios().find(space => space.space_id === spaceId) || null); }

    copiedSpaceId = signal<number | null>(null);
    // Estado al reenviar enlace de restablecimiento de contraseña
    resendingResetLink = signal(false);
    async copySpaceLink(espacio: Espacio, event: Event): Promise<void> {
      event.stopPropagation();
      const url = `${environment.clientAppUrl}/espacio/${espacio.space_id}`;
      // Espacio privado: el código NUNCA va embebido en la URL -- se copia
      // aparte, como mensaje acompañante (mismo criterio ya definido para
      // compartir por redes sociales), para que quien lo reciba sepa que
      // necesita ambas cosas.
      // Bug real corregido: con TODO el negocio privado (no el espacio
      // puntual) se copiaba el enlace sin el código, y quien lo recibía no
      // podía entrar.
      // Bug real corregido (seguridad): se copiaba "URL + salto + Código";
      // al pegarlo en la barra del navegador las líneas se unían y el código
      // quedaba DENTRO de la URL. Ahora se copia solo el enlace (lo que dice
      // el botón) y el código se muestra aparte, para compartirlo por separado.
      const businessId = this.currentUser()?.id;
      const info = businessId ? this.businessPrivacyInfo() ?? await firstValueFrom(this.businessConfigService.obtenerPrivacidad(businessId)).catch(() => null) : null;
      const isGated = !!info?.featureEnabled && (info.isPrivate || !!espacio.is_private);
      try {
        await navigator.clipboard.writeText(url);
        if (isGated && info?.accessCode) {
          this.notifications.warning(`Enlace copiado. Este espacio es privado: comparte también el código de acceso ${info.accessCode} en un mensaje aparte.`);
        }
        this.copiedSpaceId.set(espacio.space_id);
        setTimeout(() => { if (this.copiedSpaceId() === espacio.space_id) this.copiedSpaceId.set(null); }, 2000);
      } catch {
        this.notifications.error('No se pudo copiar el enlace. Cópialo manualmente.');
      }
    }
    /** Botón de acceso directo "Compartir mi sitio web" -- usa el share
     * nativo del dispositivo (hoja de compartir de verdad en celular) si
     * está disponible, y si no, copia el enlace al portapapeles, mismo
     * patrón que copySpaceLink() más abajo. El enlace se pide fresco en
     * cada clic (en vez de cachearlo) porque el slug puede haber cambiado
     * desde "Mi sitio" sin recargar el dashboard. */
    async shareMySiteWebsite(): Promise<void> {
      if (!this.cooldown.consume('share-my-site')) return;
      const businessId = this.currentUser()?.id;
      if (!businessId || this.mySiteSharing()) return;
      this.mySiteSharing.set(true);
      try {
        const site = await firstValueFrom(this.businessConfigService.obtenerSitio(businessId));
        if (!site.url) {
          this.notifications.error('Todavía no tienes un enlace disponible para tu sitio.');
          return;
        }
        if (navigator.share) {
          try {
            await navigator.share({ title: this.currentUser()?.name || 'Mi sitio', url: site.url });
            return;
          } catch (shareError: any) {
            // AbortError = el usuario cerró la hoja de compartir sin elegir
            // nada -- no es un error, no hace falta caer al portapapeles.
            if (shareError?.name === 'AbortError') return;
          }
        }
        await navigator.clipboard.writeText(site.url);
        this.notifications.success('Enlace de tu sitio copiado al portapapeles.');
      } catch (error: any) {
        this.notifications.error(friendlyError(error, 'No fue posible obtener el enlace de tu sitio.'));
      } finally {
        this.mySiteSharing.set(false);
      }
    }

    cancelDeleteSpace(): void { if (this.deletingSpace()) return; this.spacePendingDeletion.set(null); }
    confirmDeleteSpace(): void {
      const space = this.spacePendingDeletion();
      if (!space || this.deletingSpace()) return;
      this.deletingSpace.set(true);
      this.salaService.eliminarEspacio(space.space_id).subscribe({
        next: () => { this.deletingSpace.set(false); this.spacePendingDeletion.set(null); this.salaService.loadEspacios(); this.notifications.success('Espacio enviado a la Papelera.'); },
        error: async (error) => {
          const payload = error?.error;
          if (payload?.code === 'SPACE_HAS_ACTIVE_RESERVATIONS') {
            this.deletingSpace.set(false);
            const items = payload.impact?.items || [];
            const summary = items.slice(0, 6).map((item: any) => {
              const reference = item.reservation_reference
                || (item.business_reservation_number
                  ? `R-${String(item.business_reservation_number).padStart(4, '0')}`
                  : 'Reserva relacionada');
              return `${reference} · ${item.date} · ${item.customer} · ${this.formatReservationStatusLabel(item.status)}`;
            }).join('\n');
            const result = await this.dialogs.open({
              title: 'Forzar retiro de la sala',
              message: `${payload.msg}\n\nReservas afectadas (${payload.impact.reservations}):\n${summary}\n\nAl continuar se cancelarán estas reservas.`,
              confirmText: 'Cancelar reservas y retirar sala',
              danger: true,
              fields: [{ name: 'reason', label: 'Motivo obligatorio', type: 'textarea', required: true, placeholder: 'Ej: El espacio dejó de estar disponible por remodelación' }]
            });
            if (!result) return;
            this.deletingSpace.set(true);
            this.salaService.eliminarEspacio(space.space_id, { force: true, reason: String(result['reason']).trim() }).subscribe({
              next: () => { this.deletingSpace.set(false); this.spacePendingDeletion.set(null); this.salaService.loadEspacios(); this.notifications.success('Espacio enviado a la Papelera.'); },
              error: forceError => { this.deletingSpace.set(false); this.notifications.error(friendlyError(forceError, 'No fue posible completar la eliminación forzada.')); }
            });
            return;
          }
          this.deletingSpace.set(false);
          console.error('Error al enviar espacio a Reciclaje:', error);
          this.notifications.error(payload?.msg || 'No fue posible enviar el espacio a la Papelera.');
        }
      });
    }

    logout(): void {
      this.authService.logout();
      // En "Gestionar negocio" solo se cierra el modal (lo hace la ventana del super admin).
      if (this.isBusinessContextMode()) return;
      this.router.navigate(['/login']);
    }

    /** Modal de creación de espacios (ver CrearSalaComponent). */
    readonly showCreateSpace = signal(false);
    /** true: el modal abre directamente la lista de borradores. */
    readonly createSpaceWithDrafts = signal(false);

    createEspacio(): void {
      if (!this.can('spaces')) return;
      if (this.hasReachedSpaceLimit()) {
        this.showSpaceLimitModal.set(true);
        return;
      }
      this.createSpaceWithDrafts.set(false);
      this.showCreateSpace.set(true);
    }

    /** Borradores de espacios (compartidos por el negocio): se continúan en el mismo modal. */
    openSpaceDrafts(): void {
      if (!this.can('spaces')) return;
      if (this.hasReachedSpaceLimit()) {
        this.showSpaceLimitModal.set(true);
        return;
      }
      this.createSpaceWithDrafts.set(true);
      this.showCreateSpace.set(true);
    }

    effectiveSpaceLimit(): number {
      const subscription = this.currentSubscription();
      const custom = Number(subscription?.custom_max_spaces);
      const plan = Number(subscription?.planDefinition?.limits?.max_spaces);
      return Number.isInteger(custom) && custom > 0 ? custom : (Number.isFinite(plan) ? plan : 0);
    }

    hasReachedSpaceLimit(): boolean {
      if (this.currentUser()?.subscription_exempt) return false;
      const limit = this.effectiveSpaceLimit();
      const isTrial = this.currentSubscription()?.status === 'trial';
      return limit > 0 && (isTrial || limit < 9999) && this.salaService.espacios().length >= limit;
    }

    suggestedSpacePlans(): any[] {
      const limit = this.effectiveSpaceLimit();
      return this.publicPlans().filter(plan => Number(plan?.limits?.max_spaces || 0) > limit || plan?.code === 'premium').slice(0, 2);
    }

    planSpaceLimitLabel(plan: any): string {
      const limit = Number(plan?.limits?.max_spaces || 0);
      return plan?.code === 'premium' || limit >= 9999 ? 'Publicación ilimitada' : `${limit} espacios incluidos`;
    }

    closeSpaceLimitModal(): void { this.showSpaceLimitModal.set(false); }
    goToSubscriptionPlans(): void { this.closeSpaceLimitModal(); this.selectTab('suscripcion'); }
  }
